import { Vec2 } from './vec';
import { TowerType, EnemyType, GamePhase, GameCommand, CommandResult, StatusEffect } from './types';
import { Path } from './path';
import { Economy } from './economy';
import {
  BOSS_ABILITY_TYPES,
  EnemyState,
  EnemySpec,
  EmpowerBuff,
  NACHO_TYPES,
  createEnemy,
  damageEnemy,
  updateEnemy,
} from './enemy';
import {
  TowerState,
  TowerSpec,
  updateTower,
  updateMeleeTower,
  updateMidStrike,
  updateFreezeTower,
  updatePathShotTower,
  updateBoltShotTower,
  rollTowerDodge,
} from './tower';
import type { PathShotRoute } from './tower';
import {
  applyEffects,
  createProjectile,
  updateProjectile,
  updatePathShot,
  ProjectileState,
} from './projectile';
import { createFireball, updateFireball, FireballState, TOWER_MAX_HP } from './fireball';
import { CHIP_AREA, ChipState, rollChipPos } from './chip';
import { ShieldState, createShield, shieldResolved } from './shield';
import { applyTowerPoison, tickTowerPoison } from './towerPoison';
import { MINION_POSITIONS, type MinionState } from './endThem';
import { selectTarget } from './targeting';
import {
  MELEE_TOWERS,
  NO_MODIFIERS,
  SNOWSHROOM_COOLDOWN,
  SNOWSHROOM_STRIKE_DELAY,
  SNOWSHROOM_STRIKE_SHARE,
  type ModifierTable,
  type TowerModifiers,
} from '../config/effects';
import { applyStatus } from './status';
import { WaveDef, WaveRunner } from './waves';
import { arenaWaveGroups } from './arenaWave';
import { computeScore, killsPerSpawn, scriptedKills } from './scoring';
import { GameEvent } from './events';
import { Rng } from './rng';

export interface PlotDef {
  id: number;
  pos: Vec2;
}

/** One placed Level Creator decoration (render-only; the sim never reads it). */
export interface CreatorPieceDef {
  id: string;
  /** decor under the track, top over it, text (a textbox) over everything. */
  layer: 'decor' | 'top' | 'text';
  key: string;
  x: number;
  y: number;
  scale: number;
  /** Degrees, 0–360. */
  rotation: number;
  /** Colour group (#rrggbb in the art) → the colour it is painted instead. */
  colors?: Record<string, string>;
  /** Mirrored left-right (the editor's FLIP). */
  flip?: boolean;
  /** A textbox's words (layer `text` only; redacted by the server on save). */
  text?: string;
}

/** What GameScene draws for a creator level, under and over the track. */
export interface CreatorSceneDef {
  /** Plain fill behind everything, when the background is a colour. */
  bgColor?: number;
  /** Atlas key of a special background, when it is one. */
  bgKey?: string;
  /** The template's painted map, drawn over the background (absent = hidden). */
  pictureKey?: string;
  pieces: CreatorPieceDef[];
}
export interface LevelDef {
  id: number;
  name: string;
  /**
   * A player-built Level Creator level on a TEST run (spec 2026-11-01): it
   * banks nothing — no coins, stars, EXP, quest stats or achievements.
   */
  creator?: boolean;
  /** Explicit music key (creator levels; '' = silence). Others derive by id. */
  music?: string;
  /** Render-only scene for a creator level: background, picture, pieces. */
  creatorScene?: CreatorSceneDef;
  /** Campaign chapter this level belongs to (1-based). Defaults to 1. */
  chapter?: number;
  /** "HAMBI WARZ" episode numeral (e.g. 'I'). Optional; shown by the level selector. */
  episode?: string;
  /** Flavour location subtitle (e.g. 'Picnic Clearing'). Optional; shown by the level selector. */
  subtitle?: string;
  /** Atlas texture key for this level's background. Defaults to 'bg-forest'. */
  background?: string;
  /**
   * Visual style of the enemy trail. Default (undefined) is the dirt polyline;
   * 'railway' draws wooden sleepers + twin steel rails (The Mineshafts only).
   * 'rocks' draws a chain of dark stepping stones (Fiery Lava's lava crossing).
   * 'panel' draws a riveted metal walkway that blends into a hull (The Space
   * Station).
   * Render-only — the sim's Path maths is identical either way.
   */
  trackStyle?: 'railway' | 'rocks' | 'panel';
  /**
   * Extra scenery pass. 'crystals' piles procedural amethyst clusters along
   * both sides of the track and in a ring around every plot (Amethyst Core
   * only). Render-only — the sim ignores it entirely.
   */
  decor?:
    | 'crystals'
    | 'hambibar'
    | 'exhibition'
    | 'station'
    | 'beach'
    | 'nachyo'
    | 'rocketpad'
    | 'floatingrocks'
    | 'spaceplatform'
    | 'icesculptures'
    | 'phantoms'
    | 'templeghosts'
    | 'redphantoms'
    | 'evilorb'
    | 'heavenspeak'
    | 'shroomvillage'
    | 'mushystatue'
    // The Fungi finale (spec 2026-10-27).
    | 'shroomgraves'
    | 'goldstatues'
    | 'glowclusters'
    | 'treehouses';
  path: Vec2[];
  /**
   * Extra enemy entrances that MERGE into `path` (Nach'yo Business's two nacho
   * bases). Each entry is a lead-in polyline whose LAST point is exactly
   * `path[0]`, so the route an enemy walks is its lead-in followed by the whole
   * main trail — the two bases join into one track, as the brief asks.
   *
   * Wave spawns then ALTERNATE between the routes, one enemy each in turn, so
   * both lanes stay live and the plots beside either entrance both earn their
   * keep. Summoned minions inherit their summoner's route.
   *
   * Keep every lead-in the SAME length: `hubLevels.test.ts` pins it, and equal
   * lengths mean an enemy's `distance` reads the same on either lane.
   *
   * Unset (every other level) = one route, `path` itself.
   */
  entrances?: Vec2[][];
  plots: PlotDef[];
  startCash: number;
  startLives: number;
  /** Persistent coins awarded for winning this level (see ProgressService). */
  coinReward: number;
  /**
   * Rank exp paid on the FIRST win. Campaign levels leave it unset and pay by
   * chapter (see `expRewardFor` in config/levels); hub levels set it outright.
   */
  expReward?: number;
  waves: WaveDef[];
  /**
   * Rain multi-coloured confetti over the win overlay. Set on the campaign
   * finale only — every other level keeps the plain overlay. Render-only.
   */
  winConfetti?: boolean;
  /**
   * Tower HP for this level (The Hub's chapters: 10). When set, the Ember
   * Barrage tower-HP system is armed from the first tick and never disarms —
   * every tower carries a bar for the whole run, whatever is on the field.
   * Unset (the campaign): HP is armed only while a `towerFire` carrier such as
   * King Ember is alive, at TOWER_MAX_HP.
   */
  towerHp?: number;
  /**
   * Test Arena only: towers cost nothing to place or upgrade. Nothing is
   * recorded in spentByTower, so sellValue() is 0 — "sell" becomes "delete"
   * with no refund and no separate command.
   */
  freeBuild?: boolean;
  /**
   * Test Arena only: a run here is throwaway. GameScene skips ALL persistence
   * on game over (score, stars, coins, tower grants) — same treatment the demo
   * screens get.
   */
  sandbox?: boolean;
  /**
   * The Moon's PEACEFUL toggle (spec Revision 6). Never set on a level def in
   * config: the render layer sets it on its runtime copy of the level when the
   * toggle is on. Every enemy is passive — a leak costs no lives, nothing
   * throws at towers, hexes and seals never fire, no chips drop — so the run
   * cannot be lost. Stars still read off lives (always full). The coin penalty
   * (`PEACEFUL_COIN_PENALTY`) is the render layer's too: it lowers
   * `coinReward` on the same runtime copy.
   */
  peaceful?: boolean;
  /**
   * A roaming spawn: `enemyType` walks out ONCE per run, on one wave rolled
   * from the sim's seeded Rng in [firstWave, lastWave] (1-based wave numbers),
   * and it goes FIRST — it spawns at t = 0 of that wave and every group the
   * wave already had is pushed back by `leadGap` seconds. Gazing Dome's
   * Chiply (waves 13–19). Read by GameSim only.
   */
  roamer?: { enemyType: EnemyType; firstWave: number; lastWave: number; leadGap: number };
  /**
   * A level-start notice: `sender` says `text` beside the `art` portrait as
   * the level opens (Gazing Dome's chip warning, Bun Beach's whistle
   * warning). Render-only; Peaceful runs show their own notice instead.
   */
  notice?: { art: string; sender: string; text: string };
  /**
   * Mid-run changes of scene, in wave order (Rocket Rage has one; Through
   * Space and Time has two): at the START of wave `wave` the sim is paused,
   * the whole board is hidden behind a three-second cutscene of the rocket
   * leaving, and when it ends the level comes back somewhere new.
   *
   * The SIMULATION is untouched by all of this — the path, the plots and every
   * tower on them stay exactly where they were, which is the point: only the
   * ground under them changes. `background`, `trackStyle`, `decor` and
   * `plotBase` (an atlas key drawn beneath every plot — the floating rock
   * chunks) replace the level's own from that moment on.
   *
   * Render-only: GameSim never reads it.
   */
  transitions?: TransitionDef[];
  /**
   * A level-wide damage multiplier on every MELEE tower (`MELEE_TOWERS`):
   * The Shadowing Temple's 0.75, "melee towers get a 25% damage debuff". It is
   * folded into `towerMods` as `mult − 1`, ADDED to `damageMult` the way the
   * potions add — so Samurai's Taste (+35%) there nets +10%. Everything a
   * melee spec deals scales with it, Samurai Slicer's thrown daggers included.
   */
  meleeDamageMult?: number;
}

/** One entry of `LevelDef.transitions`. */
export interface TransitionDef {
  /** 1-based wave number the cutscene plays at the start of. */
  wave: number;
  background: string;
  trackStyle?: 'railway' | 'rocks' | 'panel';
  decor?: LevelDef['decor'];
  plotBase?: string;
  /** Caption while the rocket climbs. Defaults to 'The rocket is leaving...'. */
  caption?: string;
  /** Caption once it has gone and the new sky is up. Defaults to '...and so are you.'. */
  captionEnd?: string;
}
export interface BalanceTable {
  towers: Record<
    TowerType,
    // tier4 is the Evolution tier: present only for evolvable towers, and
    // reachable in a run only when the player has bought that Evolution —
    // unless tier 1 says `nativeTier4` (Ultra Hammy), see TowerSpec.
    { tier1: TowerSpec; tier2: TowerSpec; tier3: TowerSpec; tier4?: TowerSpec }
  >;
  enemies: Record<EnemyType, EnemySpec>;
}

/** Early-start cash bonus: paid for starting a wave during the build phase. */
export const EARLY_BONUS_MAX = 50;
/** Bonus decays this much per second of build-phase idling (to a floor of 0). */
export const EARLY_BONUS_DECAY = 5;

/** Current early-start bonus given `secs` elapsed in the build phase. */
export function earlyBonusFor(secs: number): number {
  return Math.max(0, Math.round(EARLY_BONUS_MAX - EARLY_BONUS_DECAY * secs));
}

/**
 * Park every melee unit a tower owns back on its plot, idle and unbound.
 *
 * Both the tier-4 Ninja's split copy (`melee2`) and the primary unit must be
 * covered: a wave can end mid-lunge, and a unit left in `out`/`back` keeps the
 * stale target id and its field position, so it renders stranded out on the
 * path for the whole build phase and starts the next wave from there.
 */
function resetMeleeUnits(t: TowerState): void {
  if (t.melee) t.melee = { phase: 'idle', pos: { ...t.pos }, targetId: null, wait: 0 };
  if (t.melee2) t.melee2 = { phase: 'idle', pos: { ...t.pos }, targetId: null, wait: 0 };
}

/**
 * A running wave must be at least this many seconds old before the player may
 * send the next wave early. Sending early runs the waves concurrently.
 */
export const EARLY_WAVE_DELAY = 2;

/**
 * Where a `towerFire.fromSky` shot launches from — off the top of the screen,
 * directly above its target. Prince Nacho's golden spears fall; they are not
 * thrown across the field from where he floats.
 */
export const SKY_LAUNCH_Y = -80;

/** Seconds a possessed tower's spirit takes to reach the tower it strikes. */
export const GHOST_STRIKE_TRAVEL = 0.7;

/**
 * Once at least one wave has been sent, a build phase lasts at most this many
 * seconds before the next wave launches itself. The FIRST wave never
 * auto-starts — the opening build is untimed. Matches the span of the
 * early-bonus decay (50 − 5/s), so tapping early is rewarded and idling
 * never pays.
 */
export const AUTO_WAVE_DELAY = 10;
export interface GameState {
  phase: GamePhase;
  cash: number;
  lives: number;
  score: number;
  waveIndex: number;
  totalWaves: number;
  enemies: EnemyState[];
  towers: TowerState[];
  enemiesDefeated: number;
  plots: PlotDef[];
  projectiles: ProjectileState[];
  /** King Ember's fireballs currently arcing towards a tower (empty otherwise). */
  fireballs: FireballState[];
  /** Chiply's Dad's dropped chips waiting to be pressed (empty otherwise). */
  chips: ChipState[];
  /** Prince Nacho's shields waiting to be held (empty otherwise). */
  shields: ShieldState[];
  /** Ultra Hammy's END THEM minions standing at the top of the screen (empty otherwise). */
  minions: MinionState[];
  /** Seconds left on END THEM's +damage for every tower (0 when not running). */
  endThemBoostFor: number;
  /** Cash bonus currently available for starting the next wave early (0 when unavailable). */
  earlyBonus: number;
  /** Whether the player may start the next wave now (build phase, or ≥EARLY_WAVE_DELAY into a wave). */
  canStartNextWave: boolean;
  /**
   * Seconds until the next wave launches itself (build phase after the first
   * wave has been sent). Null when no auto-start is pending — before the first
   * wave, mid-wave, or when no waves remain.
   */
  autoStartIn: number | null;
  /** True while a Wise Hambi map-wide freeze is active. */
  frozen: boolean;
  /**
   * Seconds left on an Imperial Decree placement seal (Emperor Pepro): while
   * above 0, placeTower is rejected. 0 whenever no seal is active.
   */
  placementLockedFor: number;
  /**
   * Test Arena only: a spawned wave is still in flight (enemies alive or more
   * still to spawn). Drives the Start Wave ↔ Clear Wave button swap.
   */
  arenaWaveActive: boolean;
  /**
   * Test Arena scoreboard: kills this attempt, and the total enemies committed
   * by every startArenaWave this attempt (counted when the wave is SENT, not as
   * enemies walk out). Both reset on clearArena — the loss overlay reads them
   * as "Defeated: kills/total". Always 0 outside the arena.
   */
  arenaKills: number;
  arenaSpawnTotal: number;
  /**
   * The 1-based wave the level's roamer (Gazing Dome's Chiply) walks out on,
   * rolled once at construction; 0 for levels without one. Informational —
   * the render layer does not tell the player.
   */
  roamerWave: number;
}

/**
 * The Ham Reaper's scythe enters just past the right edge and leaves just past
 * the left (its art is wide; spec 2026-10-12).
 */
export const SCYTHE_START_X = 1280 + 40;
export const SCYTHE_END_X = -40;

export class GameSim {
  private readonly path: Path;
  private readonly econ: Economy;
  private phase: GamePhase = 'building';
  private waveIndex = -1;
  private runners: WaveRunner[] = [];
  private enemies: EnemyState[] = [];
  private towers: TowerState[] = [];
  private nextEnemyId = 1;
  private nextTowerId = 1;
  private projectiles: ProjectileState[] = [];
  private nextProjectileId = 1;
  private fireballs: FireballState[] = [];
  private nextFireballId = 1;
  /** Chips on the playfield (see core/chip.ts). */
  private chips: ChipState[] = [];
  private nextChipId = 1;
  /** Shields on the playfield (see core/shield.ts). */
  private shields: ShieldState[] = [];
  private nextShieldId = 1;
  /**
   * Prince Nacho's laser barrage, below half health: three lasers three
   * seconds apart, each dropping one shield. While it runs BOTH his other
   * clocks are frozen — "Prince nacho can not use his other attacks during the
   * laser attacks" — and no new barrage can start. Null when none is running.
   */
  private barrage: { lasersLeft: number; nextLaserIn: number; fired: number } | null = null;
  /**
   * True once an Ember Barrage carrier has been seen alive on the field. Towers
   * gain HP at that moment and keep it for the rest of the run — including
   * towers built afterwards, which arrive at full HP.
   */
  private towerHpActive = false;
  /** HP a tower carries while the barrage is armed (the level's `towerHp`, else 3). */
  private readonly towerMaxHp: number;
  /**
   * Timed-effect multipliers (potions, the Diamond) — set by the render layer
   * each frame from the wall clock; identity by default so the balance bots
   * and every existing test see the plain balance table.
   */
  private modifiers: ModifierTable = NO_MODIFIERS;
  private enemiesDefeated = 0;
  private wavesCleared = 0;
  /** Arena scoreboard (see GameState.arenaKills / arenaSpawnTotal). */
  private arenaKills = 0;
  private arenaSpawnTotal = 0;
  private spentByTower = new Map<number, number>();
  /** Seconds elapsed in the current build phase (drives the early-start bonus). */
  private buildElapsed = 0;
  /** Seconds elapsed since the most recent wave started (gates the early send). */
  private waveElapsed = 0;
  /** Seconds remaining in the active map-wide freeze (0 when not frozen). */
  private freezeTimer = 0;
  /**
   * King Hambi's K–O rains in the air: each lands on EVERY living enemy when
   * its `delay` runs out (see `TowerSpec.shardRain`). The spec is captured at
   * cast time, so a Moon Stone that expires mid-fall does not shrink it.
   */
  private pendingRains: { delay: number; damage: number; spec: TowerSpec }[] = [];
  /** The Golden Shroom's SHROOM FRENZY in the air — the K–O's shape and rules. */
  private pendingFrenzies: { delay: number; damage: number; spec: TowerSpec }[] = [];
  /** END THEM dynamite in the air — same shape and rules as `pendingRains`. */
  private pendingEndThem: { delay: number; damage: number; spec: TowerSpec }[] = [];
  /**
   * The Ham Reaper's scythes crossing the screen (`TowerSpec.scytheSweep`):
   * the blade's x (px, moving left), its speed, and who it has already cut.
   */
  private scythes: {
    x: number;
    speed: number;
    damage: number;
    spec: TowerSpec;
    cut: Set<number>;
  }[] = [];
  /** END THEM's minions (see core/endThem.ts) and what they throw. */
  private minions: MinionState[] = [];
  private minionSpec: { spec: TowerSpec; interval: number; speed: number } | null = null;
  private nextMinionId = 1;
  /** The Ultra Hammy whose END THEM is running: selling or losing him ends it. */
  private endThemOwner: number | null = null;
  /** Seconds left on the running END THEM (boost and minions share it). */
  private endThemFor = 0;
  /** The running END THEM's +damage on every tower (0.25 = +25%). */
  private endThemBoost = 0;
  /** Seconds remaining on the Imperial Decree placement seal (0 when open). */
  private placementLockFor = 0;
  /** Sound-worthy events emitted since the last drainEvents() call. */
  private events: GameEvent[] = [];
  /** Seeded RNG — picks the Null Nugget's stun victims deterministically. */
  private readonly rng: Rng;
  /**
   * Remaining cooldown for shared-cadence hexers, keyed by enemy type (see
   * `EnemySpec.stun.shared`). One clock per type, not per enemy.
   */
  private readonly sharedStunCooldowns = new Map<EnemyType, number>();
  /**
   * Test Arena ability toggles, set per-wave by startArenaWave (and reset by
   * clearArena). While set they mute the matching enemy anti-tower pass: the
   * Null-Hex stun, the Veggie-Hex fire-rate slow, and the Imperial-Decree
   * placement seal. Campaign waves never touch them, so they are always false
   * outside the arena.
   */
  private arenaDisableStun = false;
  private arenaDisableFireSlow = false;
  private arenaDisableControl = false;
  /** The Moon Arena's Friendly toggle: mutes every towerFire carrier's throw. */
  private arenaDisableTowerFire = false;
  /** The Hub arenas' "Disable boss abilities": mutes every BOSS_ABILITY_TYPES ability. */
  private arenaDisableBoss = false;

  /** Tower types whose Evolution (tier 4) the player has purchased. */
  private readonly evolvedSet: Set<TowerType>;
  /** See GameState.roamerWave. */
  private readonly roamerWave: number;
  /**
   * One walkable Path per entrance (see `LevelDef.entrances`); `[this.path]`
   * on every single-entrance level. An enemy's `route` indexes this.
   */
  private readonly routes: Path[];
  /**
   * `routes` reduced to what a rolling cloud needs, computed once. Lightning
   * Hambi picks one of these per shot — see `updatePathShotTower`.
   */
  private readonly pathShotRoutes: PathShotRoute[];
  /** Round-robin cursor: successive wave spawns alternate between `routes`. */
  private nextRoute = 0;

  constructor(
    private readonly level: LevelDef,
    private readonly balance: BalanceTable,
    seed: number,
    evolved: Iterable<TowerType> = [],
  ) {
    this.path = new Path(level.path);
    // One Path per entrance — each the lead-in plus the whole shared trunk
    // (`path[0]` is the join, so it is not repeated). No entrances = the trunk
    // alone, which is every level but Nach'yo Business.
    this.routes = level.entrances?.length
      ? level.entrances.map((lead) => new Path([...lead, ...level.path.slice(1)]))
      : [this.path];
    this.pathShotRoutes = this.routes.map((r) => ({
      length: r.length,
      end: r.pointAt(r.length),
      start: r.pointAt(0),
    }));
    this.econ = new Economy(level.startCash, level.startLives);
    this.rng = new Rng(seed);
    this.evolvedSet = new Set(evolved);
    this.towerMaxHp = level.towerHp ?? TOWER_MAX_HP;
    // A hub level arms tower HP from the first tick and never disarms it.
    if (level.towerHp !== undefined) this.towerHpActive = true;
    // The roamer's wave is the FIRST draw on the run's Rng, so every later
    // seeded pick (hex victims, chip spots) shifts by one — only on levels
    // that have a roamer, so every other level's stream is unchanged.
    const r = level.roamer;
    this.roamerWave = r ? r.firstWave + this.rng.int(r.lastWave - r.firstWave + 1) : 0;
  }

  /**
   * The structural HP a tower of this type carries when HP is armed: its own
   * `hp` (the Market's Hambis: 15), else the level's number (The Hub: 10;
   * the campaign's Ember Barrage: TOWER_MAX_HP).
   */
  private towerHpFor(type: TowerType): number {
    return this.balance.towers[type].tier1.hp ?? this.towerMaxHp;
  }

  /** Replace the effect multipliers for the coming steps (see ../config/effects). */
  setModifiers(m: ModifierTable): void {
    this.modifiers = m;
  }

  /**
   * This tower's modifiers for the tick: the timed effects' entry for its
   * type, plus the two in-run damage buffs — a Grease Goddess covering it
   * (`TowerSpec.allyBoost`, once however many cover it) and a running END
   * THEM. All of them ADD to `damageMult`, the way the potions add to each
   * other. Undefined when nothing at all applies, as before.
   */
  private towerMods(t: TowerState): TowerModifiers | undefined {
    const base = this.modifiers.towers[t.type];
    let extra = this.endThemFor > 0 ? this.endThemBoost : 0;
    let grease = 0;
    for (const g of this.towers) {
      if (g.id === t.id) continue;
      const gs = this.spec(g);
      if (!gs.allyBoost) continue;
      if (Math.hypot(g.pos.x - t.pos.x, g.pos.y - t.pos.y) > gs.range) continue;
      grease = Math.max(grease, gs.allyBoost.damage);
    }
    extra += grease;
    // The Shadowing Temple's curse on melee (LevelDef.meleeDamageMult).
    const melee = this.level.meleeDamageMult;
    if (melee !== undefined && MELEE_TOWERS.includes(t.type)) extra += melee - 1;
    if (extra === 0) return base;
    const m = base ?? { damageMult: 1, cooldownMult: 1, slowBonus: 0 };
    return { ...m, damageMult: m.damageMult + extra };
  }

  /**
   * End a running END THEM at once: the minions leave and the boost stops.
   * Dynamite already in the air still lands — it was thrown.
   */
  private endEndThem(): void {
    const wasUp = this.minions.length > 0 || this.endThemFor > 0;
    this.minions = [];
    this.minionSpec = null;
    this.endThemOwner = null;
    this.endThemFor = 0;
    this.endThemBoost = 0;
    if (wasUp) this.events.push({ type: 'endThemEnded' });
  }

  /**
   * Ultra Hammy's END THEM clock (see `TowerSpec.endThem`): King Hambi's K–O
   * rules — the tower's own clock, a full charge HELD while the track is
   * empty. On the cast the boost and the minions start at once; the dynamite
   * lands `delay` seconds later. A second cast restarts the 20 s rather than
   * adding three more minions.
   */
  private chargeEndThem(t: TowerState, dt: number, spec: TowerSpec): void {
    const et = spec.endThem;
    if (!et) return;
    if (t.endThemCooldown === undefined) t.endThemCooldown = et.interval;
    t.endThemCooldown = Math.max(0, t.endThemCooldown - dt);
    if (t.endThemCooldown > 0) return;
    if (!this.enemies.some((e) => e.alive && !e.reachedEnd)) return;
    t.endThemCooldown = et.interval;
    this.pendingEndThem.push({ delay: et.delay, damage: et.damage, spec });
    this.endThemOwner = t.id;
    this.endThemFor = et.duration;
    this.endThemBoost = et.boost;
    // The minions throw the tier-2 Ultra Hammy's dynamite, for a flat amount.
    const tier2 = this.balance.towers[t.type].tier2;
    this.minionSpec = {
      spec: { ...tier2, damage: et.minions.damage },
      interval: et.minions.interval,
      speed: et.minions.speed,
    };
    if (this.minions.length === 0) {
      for (const pos of MINION_POSITIONS.slice(0, et.minions.count)) {
        this.minions.push({ id: this.nextMinionId++, pos: { ...pos }, cooldown: 0 });
      }
    }
    this.events.push({
      type: 'endThemCast',
      towerId: t.id,
      tier: t.tier,
      pos: { x: t.pos.x, y: t.pos.y },
      delay: et.delay,
    });
  }

  /** Land END THEM dynamite that has finished falling: `damage` to all alive. */
  private landEndThem(dt: number): void {
    if (this.pendingEndThem.length === 0) return;
    const falling: typeof this.pendingEndThem = [];
    for (const r of this.pendingEndThem) {
      r.delay -= dt;
      if (r.delay > 0) {
        falling.push(r);
        continue;
      }
      const at: Vec2[] = [];
      for (const e of this.enemies) {
        if (!e.alive || e.reachedEnd) continue;
        at.push({ x: e.pos.x, y: e.pos.y });
        applyEffects(e, r.spec, r.damage);
      }
      this.events.push({ type: 'endThemLanded', at });
    }
    this.pendingEndThem = falling;
  }

  /**
   * The minions throw: each at the enemy nearest the base, anywhere on the
   * map, led like any other shot. Then the END THEM clock runs down, and when
   * it is out the minions leave and the boost stops.
   */
  private updateMinions(dt: number): void {
    if (this.endThemFor <= 0) return;
    const ms = this.minionSpec;
    if (ms) {
      for (const m of this.minions) {
        m.cooldown = Math.max(0, m.cooldown - dt);
        if (m.cooldown > 0) continue;
        const target = selectTarget(
          m.pos,
          Infinity,
          this.enemies.filter((e) => !e.reachedEnd),
        );
        if (!target) continue;
        m.cooldown = ms.interval;
        const flight = Math.hypot(target.pos.x - m.pos.x, target.pos.y - m.pos.y) / ms.speed;
        this.projectiles.push(
          createProjectile(this.nextProjectileId++, {
            from: { x: m.pos.x, y: m.pos.y },
            to: {
              x: target.pos.x + target.vel.x * flight,
              y: target.pos.y + target.vel.y * flight,
            },
            spec: ms.spec,
            towerType: 'ultrahammy',
            tier: 2,
            speed: ms.speed,
          }),
        );
        this.events.push({ type: 'projectileFired', towerType: 'ultrahammy' });
      }
    }
    this.endThemFor = Math.max(0, this.endThemFor - dt);
    if (this.endThemFor === 0) this.endEndThem();
  }

  /**
   * The tower's spec with its effect multipliers folded in: damage (every
   * flavour of it — plain, crit/headshot rolls, the hitscan special) scaled
   * by `damageMult`, and an existing slow deepened by `slowBonus`. Returns the
   * balance-table object untouched when nothing applies.
   */
  private effectiveSpec(base: TowerSpec, mod: TowerModifiers | undefined): TowerSpec {
    if (!mod || (mod.damageMult === 1 && mod.slowBonus === 0)) return base;
    const d = mod.damageMult;
    const out: TowerSpec = { ...base, damage: base.damage * d };
    if (base.shotRoll) {
      // Each band is scaled only if the spec declares it: Mini Gunner has no
      // crit band and King Capy no headshot band, and writing an absent band
      // back as `undefined` would make it look declared to `rollShot`.
      out.shotRoll = {
        ...base.shotRoll,
        ...(base.shotRoll.crit
          ? { crit: { ...base.shotRoll.crit, damage: base.shotRoll.crit.damage * d } }
          : {}),
        ...(base.shotRoll.headshot
          ? { headshot: { ...base.shotRoll.headshot, damage: base.shotRoll.headshot.damage * d } }
          : {}),
      };
    }
    if (base.special) out.special = { ...base.special, damage: base.special.damage * d };
    if (base.shardRain) out.shardRain = { ...base.shardRain, damage: base.shardRain.damage * d };
    // The Golden Shroom's frenzy scales like the K–O it copies.
    if (base.shroomFrenzy)
      out.shroomFrenzy = { ...base.shroomFrenzy, damage: base.shroomFrenzy.damage * d };
    // The Ham Reaper's scythe is damage too.
    if (base.scytheSweep)
      out.scytheSweep = { ...base.scytheSweep, damage: base.scytheSweep.damage * d };
    // Ultra Hammy's END THEM shower scales like the K–O's rain. The minions'
    // flat 300 is a separate number and is deliberately left alone.
    if (base.endThem) out.endThem = { ...base.endThem, damage: base.endThem.damage * d };
    // Samurai Slicer's thrown daggers are damage too, not just his slash.
    if (base.midStrike) out.midStrike = { ...base.midStrike, damage: base.midStrike.damage * d };
    // Bow Hambi's poison is damage too: the whole hit is the DoT, so a Pickle
    // that left it unscaled would buff only his normal arrows.
    if (base.poisonArrow)
      out.poisonArrow = { ...base.poisonArrow, magnitude: base.poisonArrow.magnitude * d };
    if (base.slow && mod.slowBonus > 0) {
      out.slow = { ...base.slow, magnitude: Math.min(1, base.slow.magnitude + mod.slowBonus) };
    }
    return out;
  }

  /**
   * King Capy's parting gems (see `TowerSpec.deathBurst`). Called on the tick a
   * tower is destroyed, while it is still in `this.towers` — its spec and its
   * position are both read off it here.
   *
   * The gems ignore his range and go at DISTINCT enemies drawn at random from
   * everything alive on the map, each leaving the pool so one burst never hits
   * the same enemy twice; a map with fewer than `count` alive simply gets
   * fewer gems, and an empty map gets none. They are ordinary projectiles that
   * fly and land next tick, and they go out through `effectiveSpec`, so a King
   * Capy who dies under the Moon Stone bursts at +50% like his normal gems do.
   */
  /**
   * One tick of a possessed tower (EnemySpec.ghosting). When its clock runs
   * out it throws a spirit at a random OTHER tower it has not struck yet —
   * an ordinary fireball, so the Diamond's shield and the HP rules apply as
   * to any enemy's throw — and the possession lifts after its last strike.
   * A strike with no tower left to hit fizzles but still counts.
   */
  private tickPossession(t: TowerState, dt: number): void {
    const p = t.possessed;
    if (!p) return;
    p.nextIn -= dt;
    if (p.nextIn > 0) return;
    const pool = this.towers.filter(
      (o) => o.id !== t.id && (o.hp ?? 0) > 0 && !p.struck.includes(o.id),
    );
    if (pool.length > 0) {
      const victim = this.rng.pick(pool);
      p.struck.push(victim.id);
      const ball = createFireball(
        this.nextFireballId++,
        t.pos,
        victim.pos,
        victim.id,
        GHOST_STRIKE_TRAVEL,
        p.damage,
        p.projectile ?? 'proj-ghostwisp',
      );
      ball.ghosted = true;
      this.fireballs.push(ball);
      this.events.push({ type: 'fireballLaunched' });
    }
    p.hitsLeft--;
    p.nextIn = p.gap;
    if (p.hitsLeft <= 0) delete t.possessed;
  }

  private fireDeathBurst(victim: TowerState): void {
    const spec = this.effectiveSpec(this.spec(victim), this.towerMods(victim));
    const burst = spec.deathBurst;
    if (!burst) return;
    const candidates = this.enemies.filter((e) => e.alive && !e.reachedEnd);
    const speed = spec.projectileSpeed ?? 600;
    for (let n = 0; n < burst.count && candidates.length > 0; n++) {
      const target = this.rng.pick(candidates);
      candidates.splice(candidates.indexOf(target), 1);
      // Lead the target exactly as an ordinary shot does. A projectile flies to
      // a FIXED impact point and resolves on arrival, so a gem aimed at where a
      // walker stood simply lands behind it — which is why an unled burst hit
      // only the enemies that happened to be standing still.
      const flight = Math.hypot(target.pos.x - victim.pos.x, target.pos.y - victim.pos.y) / speed;
      this.projectiles.push(
        createProjectile(this.nextProjectileId++, {
          from: { x: victim.pos.x, y: victim.pos.y },
          to: {
            x: target.pos.x + target.vel.x * flight,
            y: target.pos.y + target.vel.y * flight,
          },
          spec,
          towerType: victim.type,
          tier: victim.tier,
          speed,
        }),
      );
      this.events.push({ type: 'projectileFired', towerType: victim.type });
    }
  }

  /**
   * King Hambi's K–O clock (see `TowerSpec.shardRain`). Charges on the
   * tower's own (hex- and COLA-adjusted) clock; a full charge is held until
   * something is alive on the track, then the rain is queued to land
   * `delay` seconds later and the clock starts over.
   */
  private chargeShardRain(t: TowerState, dt: number, spec: TowerSpec): void {
    const rain = spec.shardRain;
    if (!rain) return;
    if (t.rainCooldown === undefined) t.rainCooldown = rain.interval;
    t.rainCooldown = Math.max(0, t.rainCooldown - dt);
    if (t.rainCooldown > 0) return;
    if (!this.enemies.some((e) => e.alive && !e.reachedEnd)) return;
    t.rainCooldown = rain.interval;
    this.pendingRains.push({ delay: rain.delay, damage: rain.damage, spec });
    this.events.push({
      type: 'shardRainCast',
      towerId: t.id,
      tier: t.tier,
      pos: { x: t.pos.x, y: t.pos.y },
      delay: rain.delay,
    });
  }

  /**
   * The Golden Shroom's SHROOM FRENZY clock (`TowerSpec.shroomFrenzy`) — the
   * K–O's rules exactly, on its own events so it never pays a Golden Stone.
   */
  private chargeShroomFrenzy(t: TowerState, dt: number, spec: TowerSpec): void {
    const frenzy = spec.shroomFrenzy;
    if (!frenzy) return;
    if (t.frenzyCooldown === undefined) t.frenzyCooldown = frenzy.interval;
    t.frenzyCooldown = Math.max(0, t.frenzyCooldown - dt);
    if (t.frenzyCooldown > 0) return;
    if (!this.enemies.some((e) => e.alive && !e.reachedEnd)) return;
    t.frenzyCooldown = frenzy.interval;
    this.pendingFrenzies.push({ delay: frenzy.delay, damage: frenzy.damage, spec });
    this.events.push({
      type: 'shroomFrenzyCast',
      towerId: t.id,
      tier: t.tier,
      pos: { x: t.pos.x, y: t.pos.y },
      delay: frenzy.delay,
    });
  }

  /** Land every frenzy whose shrooms have finished falling: `damage` to all alive. */
  private landShroomFrenzies(dt: number): void {
    if (this.pendingFrenzies.length === 0) return;
    const falling: typeof this.pendingFrenzies = [];
    for (const r of this.pendingFrenzies) {
      r.delay -= dt;
      if (r.delay > 0) {
        falling.push(r);
        continue;
      }
      let hits = 0;
      for (const e of this.enemies) {
        if (!e.alive || e.reachedEnd) continue;
        applyEffects(e, r.spec, r.damage);
        hits++;
      }
      this.events.push({ type: 'shroomFrenzyLanded', hits });
    }
    this.pendingFrenzies = falling;
  }

  /**
   * The Ham Reaper's scythe clock (`TowerSpec.scytheSweep`) — the K–O's rules:
   * charges on the tower's own clock, a full charge waits for something on the
   * track, and the clock restarts only when the scythe actually goes.
   */
  private chargeScythe(t: TowerState, dt: number, spec: TowerSpec): void {
    const sweep = spec.scytheSweep;
    if (!sweep) return;
    if (t.scytheCooldown === undefined) t.scytheCooldown = sweep.interval;
    t.scytheCooldown = Math.max(0, t.scytheCooldown - dt);
    if (t.scytheCooldown > 0) return;
    if (!this.enemies.some((e) => e.alive && !e.reachedEnd)) return;
    t.scytheCooldown = sweep.interval;
    this.scythes.push({
      x: SCYTHE_START_X,
      speed: (SCYTHE_START_X - SCYTHE_END_X) / sweep.duration,
      damage: sweep.damage,
      spec,
      cut: new Set(),
    });
    this.events.push({
      type: 'scytheCast',
      towerId: t.id,
      tier: t.tier,
      duration: sweep.duration,
    });
  }

  /**
   * Healer Hambi's pulse (`TowerSpec.healPulse`): every `interval` seconds of
   * the tower's own clock, every tower whose plot lies within his range —
   * himself included — gets `amount` HP back, capped at its `maxHp`. Towers
   * carry HP only where the level arms it, so elsewhere nothing is healed and
   * no event is sent. Unlike the K–O, the clock never waits for the track.
   */
  private chargeHeal(t: TowerState, dt: number, spec: TowerSpec): void {
    const pulse = spec.healPulse;
    if (!pulse) return;
    if (t.healCooldown === undefined) t.healCooldown = pulse.interval;
    t.healCooldown = Math.max(0, t.healCooldown - dt);
    if (t.healCooldown > 0) return;
    t.healCooldown = pulse.interval;
    const healed: number[] = [];
    for (const o of this.towers) {
      if (o.hp === undefined || o.maxHp === undefined || o.hp <= 0 || o.hp >= o.maxHp) continue;
      if (Math.hypot(o.pos.x - t.pos.x, o.pos.y - t.pos.y) > spec.range) continue;
      o.hp = Math.min(o.maxHp, o.hp + pulse.amount);
      healed.push(o.id);
    }
    if (healed.length > 0) this.events.push({ type: 'towerHealed', towerId: t.id, healed });
  }

  /** Move every scythe left; cut each living enemy once as the blade reaches it. */
  private sweepScythes(dt: number): void {
    if (this.scythes.length === 0) return;
    const live: typeof this.scythes = [];
    for (const s of this.scythes) {
      s.x -= s.speed * dt;
      for (const e of this.enemies) {
        if (!e.alive || e.reachedEnd || s.cut.has(e.id) || e.pos.x < s.x) continue;
        applyEffects(e, s.spec, s.damage);
        s.cut.add(e.id);
      }
      if (s.x > SCYTHE_END_X) live.push(s);
      else this.events.push({ type: 'scytheEnded', hits: s.cut.size });
    }
    this.scythes = live;
  }

  /** Land every K–O whose shards have finished falling: `damage` to all alive. */
  private landShardRains(dt: number): void {
    if (this.pendingRains.length === 0) return;
    const falling: typeof this.pendingRains = [];
    for (const r of this.pendingRains) {
      r.delay -= dt;
      if (r.delay > 0) {
        falling.push(r);
        continue;
      }
      let hits = 0;
      for (const e of this.enemies) {
        if (!e.alive || e.reachedEnd) continue;
        applyEffects(e, r.spec, r.damage);
        hits++;
      }
      this.events.push({ type: 'shardRainLanded', hits });
    }
    this.pendingRains = falling;
  }

  /**
   * Chili on a tower with no slow of its own: a fresh aura over the tower's
   * range, `slowBonus` strong, applied every tick like the Cheese Guardian's.
   */
  private applyBonusSlowAura(t: TowerState, spec: TowerSpec, magnitude: number): void {
    for (const e of this.enemies) {
      if (!e.alive || e.slowImmune) continue;
      if (Math.hypot(e.pos.x - t.pos.x, e.pos.y - t.pos.y) > spec.range) continue;
      const s: StatusEffect = { kind: 'slow', magnitude, remaining: 0.4 };
      applyStatus(e.statuses, s);
    }
  }

  /**
   * True when a tower of this type may not be placed right now because it is
   * `unique` (Ultra Hammy) and one already stands. The HUD dims its row off
   * this; `placeTower` refuses it with 'only one allowed'.
   */
  placementBlocked(type: TowerType): boolean {
    return (
      this.balance.towers[type].tier1.unique === true && this.towers.some((t) => t.type === type)
    );
  }

  /**
   * Highest tier this tower type may reach in this run: 4 when Evolved, or
   * always for a tower whose fourth tier is native (Ultra Hammy).
   */
  maxTierFor(type: TowerType): 3 | 4 {
    const pair = this.balance.towers[type];
    return pair.tier4 && (pair.tier1.nativeTier4 || this.evolvedSet.has(type)) ? 4 : 3;
  }

  private spec(t: TowerState): TowerSpec {
    const pair = this.balance.towers[t.type];
    // Tiers are 1-based, the array is 0-based — hence the -1 here and at every
    // other tier→spec lookup.
    return [pair.tier1, pair.tier2, pair.tier3, pair.tier4 ?? pair.tier3][t.tier - 1];
  }

  /**
   * Fire one stun trigger: disable up to `stun.count` (default 1) random towers
   * for `stun.duration`. Stun-immune towers (the Ninja Hambi) are never
   * eligible, and each victim leaves the pool so one trigger never picks the
   * same tower twice — a short pool just means fewer victims.
   */
  private applyStun(stun: NonNullable<EnemySpec['stun']>, duration = stun.duration): void {
    const candidates = this.towers.filter(
      (t) => (t.stunnedFor ?? 0) <= 0 && !this.spec(t).stunImmune,
    );
    for (let n = 0; n < (stun.count ?? 1) && candidates.length > 0; n++) {
      const victim = this.rng.pick(candidates);
      candidates.splice(candidates.indexOf(victim), 1);
      victim.stunnedFor = duration;
      this.events.push({ type: 'towerStunned', towerType: victim.type });
    }
  }

  /**
   * Switch tower HP on (idempotent). Called the first tick an Ember Barrage
   * carrier is alive: every tower standing at that moment gains a full bar, and
   * `towerHpActive` makes every later placement arrive at full HP too.
   */
  /** True when the arena's "Disable boss abilities" toggle silences this enemy. */
  private bossMuted(type: EnemyType): boolean {
    return this.arenaDisableBoss && BOSS_ABILITY_TYPES.includes(type);
  }

  /**
   * The Snowshroom's clock (spec 2026-10-24): once ready, this tower
   * SHROOMNITIZES the nearest enemy in its range that is not already — as
   * long as there is another enemy for it to hit — and waits
   * SNOWSHROOM_COOLDOWN seconds. It holds at ready while nothing qualifies.
   * Deterministic: nearest, never a random pick, so the shared Rng is untouched.
   */
  private chargeShroomnitize(t: TowerState, dt: number, spec: TowerSpec): void {
    t.shroomnitizeIn = Math.max(0, (t.shroomnitizeIn ?? 0) - dt);
    if (t.shroomnitizeIn > 0) return;
    const alive = this.enemies.filter((e) => e.alive && !e.reachedEnd);
    if (alive.length < 2) return;
    let best: EnemyState | undefined;
    let bestD = Infinity;
    for (const e of alive) {
      if (e.shroomnitized) continue;
      const d = Math.hypot(e.pos.x - t.pos.x, e.pos.y - t.pos.y);
      if (d > spec.range && !(e.anchored && spec.range > 0)) continue;
      if (d < bestD) {
        best = e;
        bestD = d;
      }
    }
    if (!best) return;
    best.shroomnitized = { in: SNOWSHROOM_STRIKE_DELAY };
    t.shroomnitizeIn = SNOWSHROOM_COOLDOWN;
    this.events.push({ type: 'enemyShroomnitized', enemyType: best.type, pos: { ...best.pos } });
  }

  /**
   * Shroomnitized enemies count down, then each hits the nearest OTHER living
   * enemy once for SNOWSHROOM_STRIKE_SHARE of its own max HP. It is not a
   * tower hit, so it is never dodged; a kill pays its bounty in the sweep
   * that follows. With nobody left to hit, the strike fizzles.
   */
  private tickShroomnitized(dt: number): void {
    for (const e of this.enemies) {
      if (!e.shroomnitized) continue;
      if (!e.alive) {
        e.shroomnitized = undefined;
        continue;
      }
      e.shroomnitized.in -= dt;
      if (e.shroomnitized.in > 0) continue;
      e.shroomnitized = undefined;
      let target: EnemyState | undefined;
      let bestD = Infinity;
      for (const o of this.enemies) {
        if (o === e || !o.alive || o.reachedEnd) continue;
        const d = Math.hypot(o.pos.x - e.pos.x, o.pos.y - e.pos.y);
        if (d < bestD) {
          target = o;
          bestD = d;
        }
      }
      if (!target) continue;
      const damage = Math.round(e.maxHp * SNOWSHROOM_STRIKE_SHARE);
      damageEnemy(target, damage);
      this.events.push({
        type: 'enemyStruckEnemy',
        from: { ...e.pos },
        to: { ...target.pos },
        damage,
      });
    }
  }

  /**
   * Take `n` structural HP off a tower — the one place towers are hurt (a
   * landed shot, a poison tick). At 0 it is removed and its plot frees up.
   */
  private hurtTower(victim: TowerState, n: number): void {
    if (victim.hp === undefined) return;
    victim.hp = Math.max(0, victim.hp - n);
    if (victim.hp > 0) {
      this.events.push({ type: 'towerHit', towerType: victim.type });
      return;
    }
    // King Capy's parting shot, fired BEFORE he leaves the tower list — his
    // spec and his position both come off the tower that is about to stop
    // existing. See `TowerSpec.deathBurst`.
    this.fireDeathBurst(victim);
    this.towers = this.towers.filter((t) => t.id !== victim.id);
    this.spentByTower.delete(victim.id);
    if (victim.id === this.endThemOwner) this.endEndThem();
    this.events.push({ type: 'towerDestroyed', towerType: victim.type });
  }

  private armTowerHp(): void {
    if (this.towerHpActive) return;
    this.towerHpActive = true;
    for (const t of this.towers) {
      t.hp = this.towerHpFor(t.type);
      t.maxHp = this.towerHpFor(t.type);
    }
  }

  /** Whether this enemy type throws Ember Barrage fireballs at towers. */
  private isBarrageCarrier(type: EnemyType): boolean {
    return this.balance.enemies[type].towerFire !== undefined;
  }

  /**
   * Switch tower HP back off (idempotent) — called the first tick after the
   * last Ember Barrage carrier is gone. Every surviving tower loses its bar and
   * its damage: nothing is shooting at them any more, so tracking partial HP
   * would only make the NEXT King Ember start them off already wounded.
   * In-flight fireballs are dropped for the same reason.
   */
  private disarmTowerHp(): void {
    if (!this.towerHpActive) return;
    this.towerHpActive = false;
    this.fireballs = [];
    for (const t of this.towers) {
      t.hp = undefined;
      t.maxHp = undefined;
      delete t.poison;
    }
  }

  /**
   * Coins refunded for selling the tower with `towerId` — half of everything
   * spent on it (base cost + upgrades), floored. Single source of truth for the
   * sellTower reducer and the HUD's sell button label. 0 for an unknown id.
   */
  sellValue(towerId: number): number {
    return Math.floor((this.spentByTower.get(towerId) ?? 0) * 0.5);
  }

  /** A next wave exists and the player is allowed to launch it right now. */
  private get canStartNext(): boolean {
    if (this.isOver) return false;
    if (this.waveIndex + 1 >= this.level.waves.length) return false;
    if (this.phase === 'building') return true;
    return this.phase === 'wave' && this.waveElapsed >= EARLY_WAVE_DELAY;
  }

  get state(): GameState {
    return {
      phase: this.phase,
      cash: this.econ.cash,
      lives: this.econ.lives,
      score: computeScore({
        enemiesDefeated: this.enemiesDefeated,
        livesRemaining: this.econ.lives,
        wavesCleared: this.wavesCleared,
        won: this.phase === 'won',
      }),
      waveIndex: this.waveIndex,
      totalWaves: this.level.waves.length,
      enemies: this.enemies,
      towers: this.towers,
      enemiesDefeated: this.enemiesDefeated,
      plots: this.level.plots,
      projectiles: this.projectiles,
      fireballs: this.fireballs,
      chips: this.chips,
      shields: this.shields,
      minions: this.minions,
      endThemBoostFor: this.endThemFor,
      earlyBonus: this.canStartNext
        ? earlyBonusFor(this.phase === 'building' ? this.buildElapsed : this.waveElapsed)
        : 0,
      canStartNextWave: this.canStartNext,
      autoStartIn:
        this.phase === 'building' && this.waveIndex >= 0 && this.canStartNext
          ? Math.max(0, AUTO_WAVE_DELAY - this.buildElapsed)
          : null,
      frozen: this.freezeTimer > 0,
      placementLockedFor: this.placementLockFor,
      arenaWaveActive: this.runners.length > 0 || this.enemies.length > 0,
      arenaKills: this.arenaKills,
      arenaSpawnTotal: this.arenaSpawnTotal,
      roamerWave: this.roamerWave,
    };
  }

  get isOver(): boolean {
    return this.phase === 'won' || this.phase === 'lost';
  }

  /**
   * Force the run to an end state. Debug/review hook used by the demo screens to
   * surface the win/loss overlays without playing a level; not used in normal
   * play. Deterministic — only flips the phase.
   */
  forceEnd(won: boolean): void {
    this.phase = won ? 'won' : 'lost';
  }

  /**
   * `/skip` (spec 2026-10-06): end the run as a PERFECT 3★ win — every
   * scripted enemy killed, every wave cleared, every life kept — so the score
   * is what a flawless clear of THIS level earns, not a flat 750. Never lowers
   * a kill count the player already beat (summons can push past the script).
   */
  forcePerfectWin(): void {
    this.enemiesDefeated = Math.max(
      this.enemiesDefeated,
      scriptedKills(this.level, (t) => killsPerSpawn(this.balance.enemies, t)),
    );
    this.wavesCleared = this.level.waves.length;
    this.econ.resetLives(this.level.startLives);
    this.phase = 'won';
  }

  dispatch(cmd: GameCommand): CommandResult {
    switch (cmd.type) {
      case 'placeTower': {
        // Emperor Pepro's Imperial Decree: no NEW towers while the seal holds.
        if (this.placementLockFor > 0) return { ok: false, reason: 'placement sealed' };
        const plot = this.level.plots.find((p) => p.id === cmd.plotId);
        if (!plot) return { ok: false, reason: 'no such plot' };
        if (this.towers.some((t) => t.plotId === cmd.plotId))
          return { ok: false, reason: 'plot occupied' };
        // Ultra Hammy: one at a time (`TowerSpec.unique`).
        if (this.placementBlocked(cmd.towerType)) return { ok: false, reason: 'only one allowed' };
        const cost = this.level.freeBuild ? 0 : this.balance.towers[cmd.towerType].tier1.cost;
        if (!this.econ.spend(cost)) return { ok: false, reason: 'insufficient cash' };
        const id = this.nextTowerId++;
        const behavior = this.balance.towers[cmd.towerType].tier1.behavior;
        this.towers.push({
          id,
          type: cmd.towerType,
          tier: 1,
          plotId: cmd.plotId,
          pos: plot.pos,
          cooldown: 0,
          melee:
            behavior === 'melee'
              ? { phase: 'idle', pos: { x: plot.pos.x, y: plot.pos.y }, targetId: null, wait: 0 }
              : undefined,
          abilityCooldown:
            behavior === 'global-freeze'
              ? this.balance.towers[cmd.towerType].tier1.abilityCooldown
              : undefined,
          // Rebuilt mid-barrage: a fresh tower joins the fight at full HP.
          hp: this.towerHpActive ? this.towerHpFor(cmd.towerType) : undefined,
          maxHp: this.towerHpActive ? this.towerHpFor(cmd.towerType) : undefined,
        });
        this.spentByTower.set(id, cost);
        this.events.push({ type: 'towerPlaced', towerType: cmd.towerType });
        return { ok: true };
      }
      case 'upgradeTower': {
        const t = this.towers.find((x) => x.id === cmd.towerId);
        if (!t) return { ok: false, reason: 'no such tower' };
        if (t.tier >= this.maxTierFor(t.type)) return { ok: false, reason: 'already max tier' };
        const pair = this.balance.towers[t.type];
        const nextTier = (t.tier + 1) as 2 | 3 | 4;
        // maxTierFor guarantees pair.tier4 exists whenever nextTier is 4. The
        // list starts at tier 2 (the first tier you can upgrade INTO), so the
        // index is nextTier - 2.
        const cost = this.level.freeBuild
          ? 0
          : [pair.tier2, pair.tier3, pair.tier4!][nextTier - 2].upgradeCost;
        if (!this.econ.spend(cost)) return { ok: false, reason: 'insufficient cash' };
        // Wise Hambi: each tier shortens the freeze cooldown (30 → 25 → 20 →
        // 10). Upgrading must not cost the player the charge they have already
        // waited out, so carry the ELAPSED time across and re-measure it
        // against the new tier's shorter interval — the bar jumps forward, it
        // never resets. Read the old maximum before `t.tier` moves, since
        // `spec(t)` follows the tier. Clamping at 0 keeps a big carry from
        // over-filling the bar: the freeze simply becomes ready at once.
        const oldMax = this.spec(t).abilityCooldown;
        t.tier = nextTier;
        if (t.abilityCooldown !== undefined) {
          const elapsed = (oldMax ?? 0) - t.abilityCooldown;
          t.abilityCooldown = Math.max(0, (this.spec(t).abilityCooldown ?? 0) - elapsed);
        }
        // Tier-4 Ninja Evolution: the split — a second melee copy appears.
        if (nextTier === 4 && this.spec(t).behavior === 'melee' && !t.melee2) {
          t.melee2 = { phase: 'idle', pos: { x: t.pos.x, y: t.pos.y }, targetId: null, wait: 0 };
        }
        this.spentByTower.set(t.id, (this.spentByTower.get(t.id) ?? 0) + cost);
        this.events.push({ type: 'towerUpgraded', towerType: t.type, tier: nextTier });
        return { ok: true };
      }
      case 'sellTower': {
        const idx = this.towers.findIndex((x) => x.id === cmd.towerId);
        if (idx < 0) return { ok: false, reason: 'no such tower' };
        const t = this.towers[idx];
        this.econ.earn(this.sellValue(t.id));
        this.spentByTower.delete(t.id);
        this.towers.splice(idx, 1);
        // His END THEM leaves with him.
        if (t.id === this.endThemOwner) this.endEndThem();
        this.events.push({ type: 'towerSold' });
        return { ok: true };
      }
      case 'startArenaWave': {
        if (cmd.entries.length === 0) return { ok: false, reason: 'no enemies chosen' };
        if (cmd.interval <= 0) return { ok: false, reason: 'interval must be above zero' };
        for (const e of cmd.entries) {
          if (e.count < 1) return { ok: false, reason: 'count must be at least 1' };
        }
        // The entries are interleaved into one stream (see core/arenaWave.ts) and
        // spawned a fixed interval apart. Validation is all-or-nothing above, so
        // a half-built wave is impossible.
        // The arena has no wave list, so waveIndex is deliberately untouched:
        // score and wave accounting stay inert. The runner is synthetic.
        this.arenaDisableStun = cmd.disableStun ?? false;
        this.arenaDisableFireSlow = cmd.disableFireSlow ?? false;
        this.arenaDisableControl = cmd.disableControl ?? false;
        this.arenaDisableTowerFire = cmd.disableTowerFire ?? false;
        this.arenaDisableBoss = cmd.disableBossAbility ?? false;
        // The whole wave counts as "spawned" the moment it is sent — the loss
        // overlay's "Defeated: 2/10" reads against what the player committed.
        // A transforming or splitting enemy counts every kill it is worth
        // (Prince Nacho 2, Halolight 7), or the board would read "7/1".
        this.arenaSpawnTotal += cmd.entries.reduce(
          (n, e) => n + e.count * killsPerSpawn(this.balance.enemies, e.enemyType),
          0,
        );
        this.runners.push(new WaveRunner({ groups: arenaWaveGroups(cmd.entries, cmd.interval) }));
        this.waveElapsed = 0;
        this.phase = 'wave';
        this.events.push({ type: 'waveStarted' });
        return { ok: true };
      }
      case 'clearArena': {
        this.runners = [];
        this.pendingRains = [];
        this.pendingFrenzies = [];
        this.scythes = [];
        this.pendingEndThem = [];
        this.endEndThem();
        this.enemies = [];
        this.projectiles = [];
        this.fireballs = [];
        this.chips = [];
        this.freezeTimer = 0;
        this.placementLockFor = 0;
        this.waveElapsed = 0;
        this.buildElapsed = 0;
        this.arenaDisableStun = false;
        this.arenaDisableFireSlow = false;
        this.arenaDisableControl = false;
        this.arenaDisableTowerFire = false;
        this.arenaDisableBoss = false;
        // Every attempt scores from zero — the scoreboard is per-attempt.
        this.arenaKills = 0;
        this.arenaSpawnTotal = 0;
        // Structural damage is an effect like any other: the next arena run
        // starts with no barrage armed and no HP bars, exactly as a campaign
        // level does before King Ember arrives.
        this.towerHpActive = false;
        // Towers stay placed — they are what the arena exists to test — but every
        // lingering effect is wiped so the next run starts from a clean slate.
        for (const t of this.towers) {
          const spec = this.spec(t);
          t.cooldown = 0;
          t.stunnedFor = 0;
          delete t.possessed;
          delete t.poison;
          t.fireSlowFor = 0;
          t.fireSlowFactor = undefined;
          t.hp = undefined;
          t.maxHp = undefined;
          if (t.salvoQueue) t.salvoQueue = [];
          if (t.abilityCooldown !== undefined) t.abilityCooldown = spec.abilityCooldown;
          // Clearing it is the reset: updateTower lazily re-seeds it from
          // spec.special.interval on the next tick.
          t.specialCooldown = undefined;
          resetMeleeUnits(t);
        }
        this.econ.resetLives(this.level.startLives);
        this.phase = 'building';
        return { ok: true };
      }
      case 'pressChip': {
        const idx = this.chips.findIndex((c) => c.id === cmd.chipId);
        if (idx < 0) return { ok: false, reason: 'no such chip' };
        const [pressed] = this.chips.splice(idx, 1);
        this.events.push({ type: 'chipPressed', kind: pressed.kind });
        return { ok: true };
      }
      case 'holdShield': {
        const sh = this.shields.find((x) => x.id === cmd.shieldId);
        if (!sh) return { ok: false, reason: 'no such shield' };
        sh.down = cmd.down;
        return { ok: true };
      }
      case 'startNextWave': {
        if (!this.canStartNext) return { ok: false, reason: 'cannot start next wave now' };
        // Reward sending early: the bonus decays with time spent in the current
        // phase — build-phase idle, or how long the active wave has been running.
        this.econ.earn(
          earlyBonusFor(this.phase === 'building' ? this.buildElapsed : this.waveElapsed),
        );
        this.beginNextWave();
        return { ok: true };
      }
    }
  }

  /** Launch the next wave: shared by the player command and the auto-start. */
  private beginNextWave(): void {
    this.buildElapsed = 0;
    this.waveElapsed = 0;
    this.waveIndex++;
    let def: WaveDef = this.level.waves[this.waveIndex];
    // The roamer's wave: it walks out first, alone, and the wave's own groups
    // wait `leadGap` seconds behind it.
    const r = this.level.roamer;
    if (r && this.waveIndex + 1 === this.roamerWave) {
      def = {
        groups: [
          { enemyType: r.enemyType, count: 1, interval: 1, startDelay: 0 },
          ...def.groups.map((g) => ({ ...g, startDelay: g.startDelay + r.leadGap })),
        ],
      };
    }
    // A wave's own toggles (Level Creator waves) — the arena's per-wave flags,
    // carried by the WaveDef. A wave that carries `mods` sets all five (absent
    // ones live); a wave without `mods` (every shipped level) leaves them be.
    const mods = this.level.waves[this.waveIndex].mods;
    if (mods) {
      this.arenaDisableStun = mods.disableStun ?? false;
      this.arenaDisableFireSlow = mods.disableFireSlow ?? false;
      this.arenaDisableControl = mods.disableControl ?? false;
      this.arenaDisableTowerFire = mods.disableTowerFire ?? false;
      this.arenaDisableBoss = mods.disableBossAbility ?? false;
    }
    this.runners.push(new WaveRunner(def));
    this.phase = 'wave';
    this.events.push({ type: 'waveStarted' });
  }

  step(dt: number): void {
    if (this.isOver) return;

    // The Ember Barrage outlives no carrier. This is checked at the TOP of the
    // step, outside the wave branch, on purpose: by the time the last King Ember
    // has been killed and swept out of `enemies`, the wave has already cleared
    // and the phase flipped to 'building', so anything hung off the barrage pass
    // itself would never run again — the bars would stay up for the rest of the
    // run. That is exactly the Test Arena bug this guards.
    // A hub level (`towerHp` set) keeps its bars for the whole run instead.
    if (
      this.towerHpActive &&
      this.level.towerHp === undefined &&
      !this.enemies.some((e) => e.alive && this.isBarrageCarrier(e.type))
    ) {
      this.disarmTowerHp();
    }

    if (this.phase === 'building') {
      this.buildElapsed += dt;
      // Auto-start: once the campaign is rolling (≥1 wave sent), a build phase
      // lasts at most AUTO_WAVE_DELAY seconds. The bonus has fully decayed to 0
      // by then, so idling to the timer never out-earns tapping early. The
      // first wave is exempt — the opening build is untimed.
      if (this.waveIndex >= 0 && this.buildElapsed >= AUTO_WAVE_DELAY && this.canStartNext) {
        this.beginNextWave();
      }
      return;
    }
    if (this.phase === 'wave' && this.runners.length > 0) {
      this.waveElapsed += dt;
      const frozen = this.freezeTimer > 0;

      // Spawning is suspended while the map is frozen (no new enemies enter). The
      // runner clock is not advanced, so spawns are delayed, not skipped.
      if (!frozen) {
        for (const runner of this.runners) {
          for (const spawn of runner.update(dt)) {
            const spec = this.balance.enemies[spawn.enemyType];
            // Alternate the entrances, one enemy each in turn.
            const route = this.nextRoute % this.routes.length;
            this.nextRoute++;
            const spawned = createEnemy(
              this.nextEnemyId++,
              spawn.enemyType,
              spec,
              this.routes[route],
              0,
              route,
            );
            // An anchored boss sent by a wave (The Shadow Reaper) appears at his
            // own spot, not at the mouth of the trail he would be pinned to.
            if (spec.anchored && spec.anchorAt) {
              spawned.pos = { ...spec.anchorAt };
              this.events.push({ type: 'bossArrived', enemyType: spawn.enemyType });
            }
            this.enemies.push(spawned);
          }
        }
      }

      // Minion summon: Emperor Pepro tops his Mini Bell escort back up to
      // `maxAlive` every `cooldown` seconds, spawning them at his own spot on
      // the path. Mirrors the stun pass's freeze rule — paused while frozen
      // unless the summoner is slow-immune (Pepro is).
      const summoned: EnemyState[] = [];
      for (const e of this.enemies) {
        if (frozen && !e.slowImmune) continue;
        const summon = this.balance.enemies[e.type].summon;
        if (!summon || !e.alive || e.summonCooldown === undefined) continue;
        if (this.bossMuted(e.type)) continue;
        e.summonCooldown -= dt;
        if (e.summonCooldown > 0) continue;
        e.summonCooldown = summon.cooldown;
        for (const type of [summon.enemyType, ...(summon.also ?? [])]) {
          const alive =
            this.enemies.filter((x) => x.alive && x.type === type).length +
            summoned.filter((x) => x.type === type).length;
          const room = Math.min(summon.count, summon.maxAlive - alive);
          for (let i = 0; i < room; i++) {
            const spec = this.balance.enemies[type];
            // fromBase minions enter at the path start (the enemy base); the rest
            // trail the summoner slightly so a batch doesn't stack on one pixel.
            const at = summon.fromBase ? 0 : Math.max(0, e.distance - 18 * (i + 1));
            // Minions walk their summoner's lane, so a summoner on the second
            // entrance does not teleport its escort onto the first.
            const route = this.routes[e.route] ?? this.path;
            summoned.push(createEnemy(this.nextEnemyId++, type, spec, route, at, e.route));
          }
        }
      }
      // King Shroometron at a quarter health: one summon, once.
      for (const e of this.enemies) {
        const hs = this.balance.enemies[e.type].hpSummon;
        if (!hs || !e.alive || e.hpSummoned || e.hp > e.maxHp * hs.belowHpFraction) continue;
        if (this.bossMuted(e.type)) continue;
        e.hpSummoned = true;
        const route = this.routes[e.route] ?? this.path;
        for (let i = 0; i < hs.count; i++) {
          const spec = this.balance.enemies[hs.enemyType];
          const at = Math.max(0, e.distance - 18 * (i + 1));
          summoned.push(createEnemy(this.nextEnemyId++, hs.enemyType, spec, route, at, e.route));
        }
        this.events.push({ type: 'bossSummoned', enemyType: hs.enemyType });
      }
      this.enemies.push(...summoned);

      // Empower aura: while a Microwave is on the field every Microwaved
      // Nugget carries the boosted HP and throws on the boosted cadence. A
      // nugget already walking gains the HP difference the tick the carrier
      // arrives; when the last carrier dies the cap drops back and HP is
      // clamped to it. `active` is read again by the barrage pass below.
      // A carrier lists one buff per type it boosts, and each field of a buff
      // is optional — the Microwave gives the Spiced Nacho HP and a longer
      // stun, but the Moon and Laser Nachos only a faster trigger.
      const active = new Map<EnemyType, EmpowerBuff>();
      for (const e of this.enemies) {
        if (!e.alive) continue;
        for (const em of this.balance.enemies[e.type].empower ?? []) active.set(em.enemyType, em);
      }
      for (const e of this.enemies) {
        if (!e.alive) continue;
        const em = active.get(e.type);
        if (em && !e.empowered) {
          e.empowered = true;
          if (em.hp !== undefined) {
            e.hp += em.hp - e.maxHp;
            e.maxHp = em.hp;
          }
          if (e.towerFireCooldown !== undefined && em.towerFireCooldown !== undefined) {
            e.towerFireCooldown = Math.min(e.towerFireCooldown, em.towerFireCooldown);
          }
        } else if (!em && e.empowered) {
          e.empowered = false;
          e.maxHp = this.balance.enemies[e.type].hp;
          e.hp = Math.min(e.hp, e.maxHp);
        }
      }

      // Ember Barrage: King Ember's fireballs — the only enemy ability that
      // DESTROYS towers rather than disabling them. Merely having a carrier on
      // the field arms tower HP (that happens even under a freeze, so the bars
      // appear the instant he walks in). Then every `cooldown` seconds each
      // carrier lobs a ball at a random living tower. Same freeze rule as the
      // stun pass — a slow-immune carrier keeps firing through a Wise-Hambi
      // freeze — and the arena's "control effects" toggle mutes the whole pass.
      // Peaceful (The Moon's toggle): nothing throws at all.
      if (!this.arenaDisableControl && !this.arenaDisableTowerFire && !this.level.peaceful) {
        for (const e of this.enemies) {
          const fire = this.balance.enemies[e.type].towerFire;
          if (!fire || !e.alive || e.towerFireCooldown === undefined) continue;
          this.armTowerHp();
          if (this.bossMuted(e.type)) continue;
          if (frozen && !e.slowImmune) continue;
          // Prince Nacho cannot use his other attacks during a laser barrage:
          // his spear clock FREEZES mid-value rather than running on.
          if (this.barrage && this.balance.enemies[e.type].laserBarrage) continue;
          e.towerFireCooldown = Math.max(0, e.towerFireCooldown - dt);
          if (e.towerFireCooldown > 0) continue;
          // A short-reach thrower (the Zombieshroom's knife) HOLDS a charged
          // throw until a tower is within `range` of it: no draw, no reset.
          const reach = fire.range;
          if (
            reach !== undefined &&
            !this.towers.some(
              (t) => (t.hp ?? 0) > 0 && Math.hypot(t.pos.x - e.pos.x, t.pos.y - e.pos.y) <= reach,
            )
          ) {
            continue;
          }
          e.towerFireCooldown = e.empowered
            ? (active.get(e.type)?.towerFireCooldown ?? fire.cooldown)
            : fire.cooldown;
          // Space Debris: while it runs, no nacho can touch a tower at all.
          // Prince Nacho is deliberately NOT a NACHO_TYPE (see core/enemy).
          if (this.modifiers.nachoImmune && NACHO_TYPES.includes(e.type)) continue;
          const candidates = this.towers.filter(
            (t) =>
              (t.hp ?? 0) > 0 &&
              (fire.range === undefined ||
                Math.hypot(t.pos.x - e.pos.x, t.pos.y - e.pos.y) <= fire.range),
          );
          if (candidates.length === 0) continue;
          // `count` distinct victims per trigger (Prince Nacho's second form
          // takes three), each leaving the pool so one trigger never hits the
          // same tower twice — the same rule `applyStun` uses.
          // The Shadow Reaper's scythe (`everyTower`) takes the whole board.
          const hits = fire.everyTower ? candidates.length : (fire.count ?? 1);
          for (let n = 0; n < hits && candidates.length > 0; n++) {
            const victim = this.rng.pick(candidates);
            candidates.splice(candidates.indexOf(victim), 1);
            // A falling spear launches from off the top of the screen, directly
            // above its target; everything else is thrown from where it stands.
            const from = fire.fromSky ? { x: victim.pos.x, y: SKY_LAUNCH_Y } : e.pos;
            const ball = createFireball(
              this.nextFireballId++,
              from,
              victim.pos,
              victim.id,
              fire.travel,
              fire.damage,
              fire.projectile,
              fire.beam,
              fire.fromSky,
              e.id,
              fire.scale,
            );
            if (fire.swing) ball.swing = true;
            if (fire.poison) ball.poison = fire.poison;
            if (fire.possess) ball.possess = { ...fire.possess, by: e.type };
            this.fireballs.push(ball);
            this.events.push({ type: 'fireballLaunched' });
          }
        }
      }

      // Imperial Decree: a placement-locking enemy (Emperor Pepro) counts down
      // and, on expiry, seals tower placement map-wide for the duration. Same
      // freeze rule as the stun pass. The arena's per-wave "control effects"
      // toggle mutes the whole pass.
      if (!this.arenaDisableControl && !this.level.peaceful) {
        for (const e of this.enemies) {
          if (frozen && !e.slowImmune) continue;
          const lock = this.balance.enemies[e.type].placementLock;
          if (!lock || !e.alive || e.lockCooldown === undefined) continue;
          if (this.bossMuted(e.type)) continue;
          // One barrage at a time: while Prince Nacho's lasers are coming, his
          // laser clock is frozen too, so a barrage can never overlap itself.
          if (this.barrage) continue;
          e.lockCooldown -= dt;
          if (e.lockCooldown > 0) continue;
          e.lockCooldown = lock.cooldown;
          // Prince Nacho below half health: the trigger becomes a BARRAGE of
          // lasers, each with a shield, instead of the single sealing sweep.
          const bar = this.balance.enemies[e.type].laserBarrage;
          if (bar && e.hp <= e.maxHp * bar.belowHpFraction) {
            // The first laser lands on THIS tick; the rest follow `gap` apart.
            this.barrage = { lasersLeft: bar.count, nextLaserIn: 0, fired: 0 };
            continue;
          }
          this.placementLockFor = Math.max(this.placementLockFor, lock.duration);
          this.events.push({ type: 'placementLocked' });
          // The Purple Fungi seals every 5 s with no laser to fire: the
          // seal's sting alone, not the sweep's whump on top of it.
          if (e.type !== 'purplefungi') this.events.push({ type: 'laserFired' });
        }
      }

      // The barrage itself: one laser every `gap` seconds, each sealing
      // placement and dropping one shield. It ends when the last laser has
      // fired AND its shield has resolved one way or the other.
      if (this.barrage) {
        const owner = this.enemies.find(
          (e) => e.alive && this.balance.enemies[e.type].laserBarrage,
        );
        const bar = owner ? this.balance.enemies[owner.type].laserBarrage : undefined;
        const lock = owner ? this.balance.enemies[owner.type].placementLock : undefined;
        if (!owner || !bar || !lock) {
          // The carrier died mid-barrage: nothing more is coming, and any
          // shield still up is no longer anyone's problem.
          this.barrage = null;
          this.shields = [];
        } else {
          this.barrage.nextLaserIn -= dt;
          if (this.barrage.lasersLeft > 0 && this.barrage.nextLaserIn <= 0) {
            this.placementLockFor = Math.max(this.placementLockFor, lock.duration);
            this.shields.push(createShield(this.nextShieldId++, this.barrage.fired));
            this.barrage.lasersLeft--;
            this.barrage.fired++;
            this.barrage.nextLaserIn = bar.gap;
            this.events.push({ type: 'placementLocked' });
            this.events.push({ type: 'laserFired' });
            this.events.push({ type: 'shieldDropped' });
          }
          if (this.barrage.lasersLeft === 0 && this.shields.length === 0) this.barrage = null;
        }
      }

      // Shield holds: a held shield accumulates time (a release does NOT reset
      // it), and the hold must REACH `holdNeeded` before the fuse runs out.
      if (this.shields.length > 0) {
        const up: ShieldState[] = [];
        for (const sh of this.shields) {
          if (sh.down) sh.held += dt;
          if (shieldResolved(sh)) {
            this.events.push({ type: 'shieldHeld' });
            continue;
          }
          sh.remaining -= dt;
          if (sh.remaining > 0) {
            up.push(sh);
            continue;
          }
          this.econ.loseLife(sh.lives);
          this.events.push({ type: 'shieldMissed' });
        }
        this.shields = up;
      }

      if (this.placementLockFor > 0) {
        this.placementLockFor = Math.max(0, this.placementLockFor - dt);
      }

      // Press-target drops: Chiply's Dad puts a chip down every `cooldown`
      // seconds at a seeded spot clear of the plots and the trail; The
      // Lifeguard's whistles are `exclusive` — his cooldown waits while one
      // of his is still up, so they come one at a time. Targets are their
      // carrier's: the tick after it is gone, any of its still lying about
      // vanish. Same freeze rule as the other passes (the boss is slow-immune,
      // so it never applies to him), and the arena's control toggle mutes it.
      if (!this.arenaDisableControl && !this.level.peaceful) {
        const alive = new Set<number>();
        for (const e of this.enemies) {
          if (e.alive) alive.add(e.id);
          const drop = this.balance.enemies[e.type].chipDrop;
          if (!drop || !e.alive || e.chipCooldown === undefined) continue;
          if (this.bossMuted(e.type)) continue;
          if (frozen && !e.slowImmune) continue;
          if (drop.exclusive && this.chips.some((c) => c.owner === e.id)) continue;
          e.chipCooldown -= dt;
          if (e.chipCooldown > 0) continue;
          e.chipCooldown = drop.cooldown;
          const plots = this.level.plots.map((p) => p.pos);
          const kind = drop.kind ?? 'chip';
          this.chips.push({
            id: this.nextChipId++,
            kind,
            owner: e.id,
            pos: rollChipPos(this.rng, this.level.path, plots),
            remaining: drop.fuse,
            fuse: drop.fuse,
            lives: drop.lives,
          });
          this.events.push({ type: 'chipDropped', kind });
        }
        // King Shroometron's sky drop: each HP threshold crossed queues
        // `count` mushrooms, released `gap` apart, falling for `fuse` seconds.
        for (const e of this.enemies) {
          const sky = this.balance.enemies[e.type].skyDrop;
          if (!sky || !e.alive || this.bossMuted(e.type)) continue;
          const done = e.skyDropsDone ?? 0;
          if (done < sky.atHpFractions.length && e.hp <= e.maxHp * sky.atHpFractions[done]) {
            e.skyDropsDone = done + 1;
            e.skyDropQueue = { left: (e.skyDropQueue?.left ?? 0) + sky.count, nextIn: 0 };
          }
          const q = e.skyDropQueue;
          if (!q || q.left <= 0) continue;
          q.nextIn -= dt;
          if (q.nextIn > 0) continue;
          q.left--;
          q.nextIn = sky.gap;
          const x = CHIP_AREA.x0 + this.rng.next() * (CHIP_AREA.x1 - CHIP_AREA.x0);
          this.chips.push({
            id: this.nextChipId++,
            kind: 'skyshroom',
            owner: e.id,
            pos: { x, y: CHIP_AREA.y0 },
            remaining: sky.fuse,
            fuse: sky.fuse,
            lives: 0,
            towerDamage: sky.towerDamage,
          });
          this.events.push({ type: 'chipDropped', kind: 'skyshroom' });
        }
        if (this.chips.length > 0) this.chips = this.chips.filter((c) => alive.has(c.owner));
      }

      // Hypnosis (the Hyno-Fungi): every `cooldown` seconds 1–`maxTowers`
      // random towers each suffer one of four outcomes at equal odds. Every
      // draw is on the shared Rng, taken only while a Hyno-Fungi is on the
      // field, so no level without one moves. Peaceful and the arena's
      // control toggle mute it, like the ghosts.
      if (!this.arenaDisableControl && !this.level.peaceful) {
        for (const e of this.enemies) {
          if (frozen && !e.slowImmune) continue;
          const hyp = this.balance.enemies[e.type].hypnotize;
          if (!hyp || !e.alive || e.hypnoCooldown === undefined) continue;
          if (this.bossMuted(e.type)) continue;
          e.hypnoCooldown -= dt;
          if (e.hypnoCooldown > 0) continue;
          e.hypnoCooldown = hyp.cooldown;
          const pool = [...this.towers];
          const n = 1 + Math.floor(this.rng.next() * hyp.maxTowers);
          for (let k = 0; k < n && pool.length > 0; k++) {
            const victim = this.rng.pick(pool);
            pool.splice(pool.indexOf(victim), 1);
            const roll = Math.floor(this.rng.next() * 4);
            const outcome = (['stun', 'shroomnitize', 'range', 'stunseal'] as const)[roll];
            this.events.push({
              type: 'towerHypnotized',
              towerType: victim.type,
              pos: { ...victim.pos },
              outcome,
            });
            if (outcome === 'range') {
              victim.rangeShrinkFor = hyp.rangeFor;
              victim.rangeShrinkMult = hyp.rangeMult;
              continue;
            }
            if (outcome === 'shroomnitize') {
              if (victim.possessed) continue;
              victim.possessed = {
                by: e.type,
                hitsLeft: hyp.possess.hits,
                damage: hyp.possess.damage,
                nextIn: hyp.possess.first,
                gap: hyp.possess.gap,
                struck: [],
                projectile: hyp.possess.projectile,
              };
              this.events.push({ type: 'towerPossessed', towerType: victim.type, by: e.type });
              continue;
            }
            // stun, or stun + seal. The Ninja Hambi's stun immunity holds.
            if (!this.spec(victim).stunImmune) {
              victim.stunnedFor = Math.max(victim.stunnedFor ?? 0, hyp.stun);
              this.events.push({ type: 'towerStunned', towerType: victim.type });
            }
            if (outcome === 'stunseal') {
              this.placementLockFor = Math.max(this.placementLockFor, hyp.seal);
              this.events.push({ type: 'placementLocked' });
            }
          }
        }
      }
      // Fuses burn down; an unpressed target costs `lives` lives (K.O / STRIKE).
      if (this.chips.length > 0) {
        const lit: ChipState[] = [];
        for (const c of this.chips) {
          c.remaining -= dt;
          if (c.remaining > 0) {
            lit.push(c);
            continue;
          }
          if (c.towerDamage !== undefined) {
            const standing = this.towers.filter((t) => (t.hp ?? 0) > 0);
            if (standing.length > 0) this.hurtTower(this.rng.pick(standing), c.towerDamage);
          } else {
            this.econ.loseLife(c.lives);
          }
          this.events.push({ type: 'chipExpired', kind: c.kind });
        }
        this.chips = lit;
      }

      // Null Hex: stunning enemies (the Null Nugget) count down and, on
      // expiry, disable one random not-yet-stunned tower. Paused while the map
      // is frozen — a Wise Hambi freeze stops the boss's hex too — except for a
      // slow-immune enemy (King Nugget-tron), which the freeze cannot touch.
      // The arena's per-wave "no stuns" toggle mutes the whole pass.
      if (!this.arenaDisableStun && !this.level.peaceful) {
        // Per-enemy hexers: every carrier runs its own clock.
        for (const e of this.enemies) {
          if (frozen && !e.slowImmune) continue;
          const stun = this.balance.enemies[e.type].stun;
          if (!stun || stun.shared || !e.alive || e.stunCooldown === undefined) continue;
          e.stunCooldown -= dt;
          if (e.stunCooldown > 0) continue;
          e.stunCooldown = stun.cooldown;
          // Space Debris jams every nacho: the clock still runs, no tower
          // locks up. An empowered Spiced Nacho holds towers the Microwave's
          // longer `stunDuration` instead of its own.
          if (this.modifiers.nachoImmune && NACHO_TYPES.includes(e.type)) continue;
          const boost = e.empowered ? active.get(e.type)?.stunDuration : undefined;
          this.applyStun(stun, boost ?? stun.duration);
        }

        // Shared-cadence hexers (the Esparagus Mage): one clock per enemy TYPE,
        // ticked once per step while at least one carrier is active, so extra
        // carriers make the hex no more frequent — only the wave harder to
        // clear. The clock is dropped when the last carrier leaves the field,
        // so the next group starts on a fresh cooldown.
        const activeShared = new Set<EnemyType>();
        for (const e of this.enemies) {
          if (frozen && !e.slowImmune) continue;
          if (!e.alive) continue;
          if (this.balance.enemies[e.type].stun?.shared) activeShared.add(e.type);
        }
        for (const type of this.sharedStunCooldowns.keys()) {
          if (!activeShared.has(type)) this.sharedStunCooldowns.delete(type);
        }
        for (const type of activeShared) {
          const stun = this.balance.enemies[type].stun;
          if (!stun) continue;
          const next = (this.sharedStunCooldowns.get(type) ?? stun.cooldown) - dt;
          if (next > 0) {
            this.sharedStunCooldowns.set(type, next);
            continue;
          }
          this.sharedStunCooldowns.set(type, stun.cooldown);
          this.applyStun(stun);
        }
      }

      // Ghosting (Jack'O Ghost, the Blue Ghost): every `cooldown` seconds a
      // carrier possesses one random tower that is not possessed already. The
      // possession itself — the tower's strikes at its neighbours — runs in
      // the tower loop below. Same freeze rule as the stun pass; the arena's
      // control toggle and Peaceful mute it.
      if (!this.arenaDisableControl && !this.level.peaceful) {
        for (const e of this.enemies) {
          if (frozen && !e.slowImmune) continue;
          const ghost = this.balance.enemies[e.type].ghosting;
          if (!ghost || !e.alive || e.ghostCooldown === undefined) continue;
          e.ghostCooldown -= dt;
          if (e.ghostCooldown > 0) continue;
          e.ghostCooldown = ghost.cooldown;
          const candidates = this.towers.filter((t) => !t.possessed);
          if (candidates.length === 0) continue;
          const victim = this.rng.pick(candidates);
          victim.possessed = {
            by: e.type,
            hitsLeft: ghost.hits,
            damage: ghost.damage,
            nextIn: ghost.first,
            gap: ghost.gap,
            struck: [],
          };
          this.events.push({ type: 'towerPossessed', towerType: victim.type, by: e.type });
        }
      }

      // Veggie Hex: the Brocco slows one random tower's fire rate. Mirrors the
      // stun pass (same cadence, seeded victim, pauses under a freeze) but
      // reduces fire rate instead of disabling — and NO tower is immune.
      // The arena's per-wave "no fire-rate slows" toggle mutes the whole pass.
      if (!this.arenaDisableFireSlow && !this.level.peaceful) {
        for (const e of this.enemies) {
          if (frozen && !e.slowImmune) continue;
          const hex = this.balance.enemies[e.type].fireRateHex;
          if (!hex || !e.alive || e.hexCooldown === undefined) continue;
          e.hexCooldown -= dt;
          if (e.hexCooldown > 0) continue;
          e.hexCooldown = hex.cooldown;
          const candidates = this.towers.filter((t) => (t.fireSlowFor ?? 0) <= 0);
          if (candidates.length === 0) continue;
          const victim = this.rng.pick(candidates);
          victim.fireSlowFor = hex.duration;
          victim.fireSlowFactor = hex.slow;
          this.events.push({ type: 'towerSlowed', towerType: victim.type });
        }
      }

      for (const t of this.towers) {
        // A Hyno-Fungi's shrunken range wears off on real time, stunned or not.
        if ((t.rangeShrinkFor ?? 0) > 0 && !frozen) {
          t.rangeShrinkFor = Math.max(0, (t.rangeShrinkFor ?? 0) - dt);
          if (t.rangeShrinkFor === 0) t.rangeShrinkMult = undefined;
        }
        // A stunned tower does nothing this tick: no shots, no aura, no melee,
        // and no ability-cooldown progress (its timers freeze mid-value). The
        // stun itself only wears off while the map is running.
        if ((t.stunnedFor ?? 0) > 0) {
          if (!frozen) t.stunnedFor = Math.max(0, (t.stunnedFor ?? 0) - dt);
          if ((t.stunnedFor ?? 0) > 0 || frozen) continue;
        }
        // A possessed tower attacks no enemy: it only counts down to its
        // strikes at the other towers (EnemySpec.ghosting), then is freed.
        if (t.possessed) {
          if (!frozen) this.tickPossession(t, dt);
          continue;
        }
        // A Veggie-Hexed tower advances its firing clock at reduced speed (a
        // 50% slow => half rate). The timer drains on real time and pauses
        // under a freeze, exactly like the stun above.
        let tdt = dt;
        if ((t.fireSlowFor ?? 0) > 0) {
          if (!frozen) t.fireSlowFor = Math.max(0, (t.fireSlowFor ?? 0) - dt);
          tdt = dt * (1 - (t.fireSlowFactor ?? 0));
        }
        // Timed effects: COLA runs the firing clock faster; Pickle / Burger
        // scale the damage and Chili deepens (or adds) a slow — see
        // effectiveSpec. Identity when no effect touches this tower type.
        const mod = this.towerMods(t);
        if (mod) tdt *= mod.cooldownMult;
        const scaled = this.effectiveSpec(this.spec(t), mod);
        // Hypnotised short: every behaviour below reads `spec.range`.
        const spec =
          (t.rangeShrinkFor ?? 0) > 0 && t.rangeShrinkMult !== undefined
            ? { ...scaled, range: scaled.range * t.rangeShrinkMult }
            : scaled;
        if (mod && mod.slowBonus > 0 && !spec.slow && !frozen) {
          this.applyBonusSlowAura(t, spec, mod.slowBonus);
        }
        if (mod?.shroomnitize && !frozen) this.chargeShroomnitize(t, dt, spec);
        if (spec.shardRain) this.chargeShardRain(t, tdt, spec);
        if (spec.shroomFrenzy) this.chargeShroomFrenzy(t, tdt, spec);
        if (spec.scytheSweep) this.chargeScythe(t, tdt, spec);
        if (spec.healPulse) this.chargeHeal(t, tdt, spec);
        if (spec.endThem) this.chargeEndThem(t, tdt, spec);
        if (spec.behavior === 'global-freeze') {
          // The ability cooldown pauses during a freeze; only advance it when the
          // map is running. A fresh trigger refreshes the freeze to full duration.
          if (!frozen && updateFreezeTower(t, tdt, spec)) {
            this.freezeTimer = Math.max(this.freezeTimer, spec.freezeDuration ?? 0);
          }
          continue;
        }
        if (spec.behavior === 'melee') {
          if (updateMeleeTower(t, tdt, spec, this.enemies)) {
            this.events.push({ type: 'projectileFired', towerType: t.type });
            // Samurai Slicer: a landed slash arms his dagger roll.
            if (spec.midStrike) t.midStrikeIn = spec.midStrike.delay;
          }
          if (spec.midStrike) {
            for (const spawn of updateMidStrike(t, tdt, spec, this.enemies, this.rng)) {
              this.projectiles.push(createProjectile(this.nextProjectileId++, spawn));
              this.events.push({ type: 'projectileFired', towerType: t.type });
            }
          }
          continue;
        }
        // Lightning Hambi: the cloud leaves from the BASE, whatever plot he
        // stands on, and rolls back up the track (see updatePathShot).
        if (spec.behavior === 'path-shot') {
          // An anchored boss stands OFF the track, well outside the rolling
          // cloud's hit radius, so against one the bolt is thrown straight from
          // the plot instead (see updateBoltShotTower).
          const offTrack = this.enemies.find((e) => e.alive && e.anchored);
          const spawn = offTrack
            ? updateBoltShotTower(t, tdt, spec, offTrack)
            : updatePathShotTower(t, tdt, spec, this.enemies, this.pathShotRoutes, this.rng);
          if (spawn) {
            this.projectiles.push(createProjectile(this.nextProjectileId++, spawn));
            this.events.push({ type: 'projectileFired', towerType: t.type });
          }
          continue;
        }
        for (const spawn of updateTower(t, tdt, spec, this.enemies, this.rng)) {
          // Hitscan special (Phantom Hambi): the damage lands NOW, through the
          // same applyEffects path a projectile impact uses, so the kill/bounty
          // sweep below counts it this very tick. No projectile spawns; the
          // render layer's cinematic rides the specialFired event instead.
          if (spawn.hitscan && spec.special) {
            const victim = this.enemies.find((e) => e.id === spawn.targetId && e.alive);
            if (victim) {
              applyEffects(victim, spec, spec.special.damage);
              this.events.push({
                type: 'specialFired',
                towerType: t.type,
                tier: t.tier,
                from: { x: spawn.from.x, y: spawn.from.y },
                to: { x: victim.pos.x, y: victim.pos.y },
              });
            }
            continue;
          }
          this.projectiles.push(createProjectile(this.nextProjectileId++, spawn));
          this.events.push({
            type: 'projectileFired',
            towerType: t.type,
            ...(spawn.poison ? { poison: true } : {}),
          });
        }
      }

      this.landShardRains(dt);
      this.landShroomFrenzies(dt);
      this.sweepScythes(dt);
      this.landEndThem(dt);
      this.updateMinions(dt);

      // Enemies: DoT ticks always; movement is suspended while frozen.
      for (const e of this.enemies) {
        updateEnemy(e, dt, this.routes[e.route] ?? this.path, frozen);
      }

      if (this.freezeTimer > 0) this.freezeTimer = Math.max(0, this.freezeTimer - dt);

      // Advance projectiles AFTER enemies move (so dodges are real) and BEFORE
      // the bounty/lives sweep (so kills are counted this tick).
      const survivors: ProjectileState[] = [];
      for (const p of this.projectiles) {
        const flying =
          p.pathDist !== undefined
            ? // Up the lead-in it rolled for, not the bare trunk — which would
              // strand it at the junction on a multi-entrance map.
              updatePathShot(p, dt, this.routes[p.route ?? 0] ?? this.path, this.enemies)
            : updateProjectile(p, dt, this.enemies);
        if (flying) {
          survivors.push(p);
        } else if (p.towerType === 'bomb') {
          // The bomb reached its landing point this tick — explode (boom + anim).
          this.events.push({ type: 'projectileImpact', towerType: 'bomb' });
        } else if (p.towerType === 'demonic') {
          // Demonic Hambi's skull blew up (dark purple, like the bomb).
          this.events.push({ type: 'projectileImpact', towerType: 'demonic' });
        } else if (p.towerType === 'ultrahammy' && (p.tier === 2 || p.tier === 3)) {
          // Ultra Hammy's dynamite (tiers 2–3, and his minions) goes off too.
          this.events.push({ type: 'projectileImpact', towerType: 'ultrahammy' });
        } else if (p.towerType === 'aquasorcerer' && p.didHit) {
          // The water ball burst on an enemy — splash. A droplet that whiffs
          // hits nothing and nothing to splash on, so it stays silent.
          this.events.push({ type: 'projectileImpact', towerType: 'aquasorcerer' });
        } else if (p.didHit && (p.poison || p.shotKind === 'crit' || p.shotKind === 'headshot')) {
          // A roll that beat the baseline AND connected — the render layer pops
          // bubble text here (POISON! for Bow Hambi's arrow). A whiff stays quiet.
          this.events.push({
            type: 'critHit',
            kind: p.poison ? 'poison' : (p.shotKind as 'crit' | 'headshot'),
            towerType: p.towerType,
            pos: { x: p.to.x, y: p.to.y },
          });
        }
      }
      this.projectiles = survivors;

      // Fireballs land: take an HP off the tower they were aimed at, and remove
      // it outright at 0 — which frees its plot, since occupancy is derived from
      // the tower list. A ball aimed at a tower that has since been sold or
      // already destroyed simply fizzles.
      const flying: FireballState[] = [];
      for (const f of this.fireballs) {
        if (updateFireball(f, dt)) {
          flying.push(f);
          continue;
        }
        const victim = this.towers.find((t) => t.id === f.targetTowerId);
        if (!victim || victim.hp === undefined) continue;
        // The Diamond's shield: the ball lands, nothing happens.
        if (this.modifiers.invulnerable) continue;
        // The Shroom: the throw misses outright — no damage, poison or
        // possession — and "DODGE" floats over the tower.
        const dodge = this.modifiers.towers[victim.type]?.dodgeChance ?? 0;
        if (dodge > 0 && rollTowerDodge(victim, dodge)) {
          this.events.push({ type: 'towerDodged', towerType: victim.type, pos: { ...victim.pos } });
          continue;
        }
        // A Poison Shroom's dose takes hold (a fresh one refreshes it) before
        // the impact, so a tower the impact destroys just takes it with it.
        if (f.poison) {
          victim.poison = applyTowerPoison(f.poison);
          this.events.push({ type: 'towerPoisoned', towerType: victim.type });
        }
        // A Glowshroom's potion shroomnitizes it: the ghosts' possession, so
        // `tickPossession` runs the strikes. Not over a possession already
        // running, and muted with the rest of control by the arena toggle.
        if (f.possess && !victim.possessed && !this.arenaDisableControl) {
          victim.possessed = {
            by: f.possess.by,
            hitsLeft: f.possess.hits,
            damage: f.possess.damage,
            nextIn: f.possess.first,
            gap: f.possess.gap,
            struck: [],
            projectile: f.possess.projectile,
          };
          this.events.push({ type: 'towerPossessed', towerType: victim.type, by: f.possess.by });
        }
        if (f.damage > 0) this.hurtTower(victim, f.damage);
      }
      this.fireballs = flying;

      // Tower poison ticks on by itself. The Diamond's shield holds the ticks
      // off too; a dose on a tower that has gone simply went with it.
      for (const t of [...this.towers]) {
        if (!t.poison || t.hp === undefined) continue;
        const tick = tickTowerPoison(t.poison, dt);
        if (tick.done) t.poison = undefined;
        if (tick.damage > 0 && !this.modifiers.invulnerable) this.hurtTower(t, tick.damage);
      }

      // Second forms spawned by anything that died THIS tick. They must join
      // `enemies` before the win check below, or a level whose last enemy is a
      // transforming boss ends on a technicality one tick early.
      // Snowshroom: a shroomnitized enemy hits the nearest other enemy once.
      if (!frozen) this.tickShroomnitized(dt);
      // The Mushy's dodges this tick, one "DODGE" each over his head.
      for (const e of this.enemies) {
        if (!e.dodged) continue;
        this.events.push({ type: 'enemyDodged', enemyType: e.type, pos: { ...e.pos } });
        e.dodged = 0;
      }
      const secondForms: NonNullable<EnemySpec['transformsInto']>[] = [];
      const splits: EnemyState[] = [];
      // Banking Burger: his bonus on every kill while he stands (he is
      // `unique`, so at most one pays). Looked up once per tick.
      const banker = this.towers.find((t) => this.spec(t).killBonus);
      const bankerCash = banker ? (this.spec(banker).killBonus?.cash ?? 0) : 0;
      for (const e of this.enemies) {
        if (!e.alive && !e.reachedEnd) {
          this.econ.earn(e.bounty);
          if (banker && bankerCash > 0) {
            this.econ.earn(bankerCash);
            this.events.push({
              type: 'bankerPaid',
              towerId: banker.id,
              cash: bankerCash,
              pos: { ...e.pos },
            });
          }
          this.enemiesDefeated++;
          this.arenaKills++;
          this.events.push({ type: 'enemyKilled', enemyType: e.type });
          // Killed, not leaked: Prince Nacho gets back up bigger.
          const tf = this.balance.enemies[e.type].transformsInto;
          if (tf) secondForms.push(tf);
          // Halolight breaks into smaller Halolights where it fell, on its
          // own route, spread either side of him so they don't stack.
          const split = this.balance.enemies[e.type].splitsInto;
          if (split) {
            const spec = this.balance.enemies[split.enemyType];
            const route = this.routes[e.route] ?? this.path;
            const step = split.count > 1 ? split.spread / (split.count - 1) : 0;
            for (let i = 0; i < split.count; i++) {
              const off = (i - (split.count - 1) / 2) * step;
              const at = Math.min(route.length - 1, Math.max(0, e.distance + off));
              splits.push(
                createEnemy(this.nextEnemyId++, split.enemyType, spec, route, at, e.route),
              );
            }
            this.events.push({ type: 'enemySplit', enemyType: e.type });
          }
        }
        if (e.reachedEnd) {
          // Peaceful: the walk-off still counts as a leak (and sounds like
          // one), it just costs nothing.
          if (!this.level.peaceful) this.econ.loseLife(e.livesCost);
          this.events.push({ type: 'enemyLeaked', enemyType: e.type });
        }
      }
      this.enemies = this.enemies.filter((e) => e.alive);
      this.enemies.push(...splits);

      for (const tf of secondForms) {
        const spec = this.balance.enemies[tf.enemyType];
        const spawned = createEnemy(this.nextEnemyId++, tf.enemyType, spec, this.path);
        // An anchored enemy is placed outright rather than walked to: it lands
        // where the spec says, not at the mouth of the trail.
        spawned.pos = { ...tf.at };
        this.enemies.push(spawned);
        // "when he spawns no enemys spawn on the track" — whatever the wave had
        // left to send is cancelled. Anything already walking is still yours to
        // kill, and the win check still waits for all of it.
        //
        // Cancelled, NOT dropped: the wave branch is gated on
        // `runners.length > 0`, so emptying the list would stop the whole
        // simulation dead with the boss standing there.
        // A sandbox arena keeps sending what the player asked for.
        if (!this.level.sandbox) for (const r of this.runners) r.cancelRemaining();
        this.events.push({ type: 'bossTransformed', enemyType: tf.enemyType });
      }

      if (this.econ.dead) {
        this.projectiles = [];
        this.pendingRains = [];
        this.pendingFrenzies = [];
        this.scythes = [];
        this.pendingEndThem = [];
        this.endEndThem();
        this.fireballs = [];
        this.chips = [];
        this.shields = [];
        this.barrage = null;
        this.phase = 'lost';
        this.events.push({ type: 'lost' });
        return;
      }
      if (this.runners.every((r) => r.finished) && this.enemies.length === 0) {
        this.projectiles = [];
        this.pendingRains = [];
        this.pendingFrenzies = [];
        this.scythes = [];
        // Dynamite still in the air has nothing left to land on. The minions
        // STAY: the brief gives them 20 s, and a wave the shower cleared would
        // otherwise send them home the frame they arrived. The build phase
        // does not step them, so their clock waits for the next wave.
        this.pendingEndThem = [];
        // No fireball outlives the boss who threw it.
        this.fireballs = [];
        this.runners = [];
        // The decree dies with its emperor — no seal outlives its wave.
        this.placementLockFor = 0;
        // Nor does a shield outlive the boss who summoned it.
        this.shields = [];
        this.barrage = null;
        // Every wave started so far has now fully cleared.
        this.wavesCleared = this.waveIndex + 1;
        // The arena has no wave list, so "ran out of waves" must NOT read as a
        // win there — it only means the spawned wave finished. It falls into the
        // build branch below and waits for the next Start Wave; only running out
        // of lives ends an arena run.
        if (!this.level.sandbox && this.waveIndex + 1 >= this.level.waves.length) {
          this.phase = 'won';
          this.events.push({ type: 'won' });
        } else {
          this.phase = 'building';
          // A freeze must not persist across a wave boundary — each wave
          // should start un-frozen, even if a Wise Hambi freeze was still
          // active the instant the last enemies cleared.
          this.freezeTimer = 0;
          // Between waves, snap any roaming melee units back to their base so
          // none freeze mid-field if a wave ended during a lunge.
          for (const t of this.towers) {
            resetMeleeUnits(t);
            if (t.salvoQueue) t.salvoQueue = [];
            // No stun outlives its wave — every build phase starts clean.
            if (t.stunnedFor) t.stunnedFor = 0;
            // Nor does a possession: the ghost that cast it is gone.
            if (t.possessed) delete t.possessed;
            // …nor any Poison Shroom dose: its last tick never lands on the
            // next wave, with no shroom on the board.
            if (t.poison) delete t.poison;
          }
          this.events.push({ type: 'waveCleared' });
        }
      }
    }
  }

  /** Return and clear the sound events buffered since the last call. */
  drainEvents(): GameEvent[] {
    const out = this.events;
    this.events = [];
    return out;
  }
}
